import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import toast from 'react-hot-toast';
import logo from '../../assets/FinalLOGO.png';
import {
  CalendarClock, Activity, User, Phone, Mail, MapPin, Calendar, Clock,
  Shield, FileText, CheckCircle2, AlertCircle, Building2, Stethoscope,
  Wallet, CreditCard, Tag, ArrowLeft, Download, Sparkles, Key, Check,
  Share2, Receipt, HeartPulse, Copy, ExternalLink, ChevronRight, Hash,
  FileCheck, AlertTriangle, Syringe, Bed, Droplet, UserCheck, ShieldAlert,
  ClipboardList, ArrowUpRight, PhoneCall, PhoneMissed, PhoneOff, PhoneIncoming,
  Edit, Loader2, RefreshCw, X, Pill, HeartHandshake, IndianRupee,
  XCircle, RotateCcw, Globe
} from 'lucide-react';
import CallHistoryPanel, { getEffectiveDisposition, getLightStatusBadge, getLightDispositionBadge } from '../leads/components/CallHistoryPanel';
import { getCallLogs, initiateCall as exotelInitiateCall, saveCallDisposition } from '../../services/exotelApi';
import EditPatientModal from '../../Components/common/EditPatientModal';
import { getEncryptedLeadId } from '../../utils/routeEncryption';


const getSavedPipeline = (paramId, leadId, surg) => {
  try {
    const mapStr = localStorage.getItem('doxez_pipeline_selections');
    const map = mapStr ? JSON.parse(mapStr) : {};

    const keys = [
      paramId,
      leadId,
      surg?._id,
      typeof surg?.lead === 'object' ? surg?.lead?._id : surg?.lead,
      surg?.bookingId
    ].filter(Boolean);

    for (const k of keys) {
      if (map[k]) return map[k];
      const singleVal = localStorage.getItem(`selected_pipeline_${k}`);
      if (singleVal) return singleVal;
    }
  } catch (e) {
    console.error("Error reading pipeline map", e);
  }

  return null;
};

const savePipelineChoice = (choice, paramId, leadId, surg) => {
  try {
    const mapStr = localStorage.getItem('doxez_pipeline_selections');
    const map = mapStr ? JSON.parse(mapStr) : {};

    const keys = [
      paramId,
      leadId,
      surg?._id,
      typeof surg?.lead === 'object' ? surg?.lead?._id : surg?.lead,
      surg?.bookingId
    ].filter(Boolean);

    keys.forEach(k => {
      map[k] = choice;
      localStorage.setItem(`selected_pipeline_${k}`, choice);
    });

    localStorage.setItem('doxez_pipeline_selections', JSON.stringify(map));
  } catch (e) {
    console.error("Error saving pipeline map", e);
  }
};

const detectPipeline = (paramId, leadId, surg, searchParams) => {
  const saved = getSavedPipeline(paramId, leadId, surg);
  if (saved) return saved;

  const origin = searchParams.get('origin') || searchParams.get('from');
  if (origin === 'appointments' || origin === 'appointment') return 'appointment';
  if (origin === 'surgical') return 'surgical';
  if (origin === 'ophe') return 'ophe';

  return null;
};

const SurgeryDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const urlLeadId = searchParams.get('leadId') || '';
  const [surgery, setSurgery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [postOperativeNotes, setPostOperativeNotes] = useState('');
  const [outcome, setOutcome] = useState('successful');
  const [activeTab, setActiveTab] = useState('clinical');
  const [selectedPipeline, setSelectedPipeline] = useState(() => {
    return detectPipeline(id, urlLeadId, null, searchParams);
  });
  const [hasActualSurgery, setHasActualSurgery] = useState(false);
  const [copiedId, setCopiedId] = useState(false);
  const [opheCatalog, setOpheCatalog] = useState([]);
  const [confirmingOphe, setConfirmingOphe] = useState(false);

  useEffect(() => {
    api.get('/ophe/services')
      .then(res => {
        if (res.data?.success && res.data?.data) {
          setOpheCatalog(res.data.data);
        }
      })
      .catch(err => console.error("Error fetching OPHE services:", err));
  }, []);

  // Call Tracking & Disposition State
  const [latestCall, setLatestCall] = useState(null);
  const [callLogs, setCallLogs] = useState([]);
  const [callHistoryRefresh, setCallHistoryRefresh] = useState(0);
  const [isCallingPatient, setIsCallingPatient] = useState(false);
  const [showQuickDispModal, setShowQuickDispModal] = useState(false);
  const [showEditPatientModal, setShowEditPatientModal] = useState(false);
  const canEditPatient = user?.role === 'admin' || user?.role === 'customer_care' || user?.role === 'cc_head' || user?.role === 'accountant';
  const [quickDispForm, setQuickDispForm] = useState({
    disposition: 'callback_completed',
    dispositionNotes: '',
    callReason: ''
  });
  const [isSavingQuickDisp, setIsSavingQuickDisp] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('Patient Not Interested / Declined');
  const [rejectNotes, setRejectNotes] = useState('');
  const [isRejecting, setIsRejecting] = useState(false);
  const [isRestoringLead, setIsRestoringLead] = useState(false);

  const targetLeadId = surgery?.lead?._id || (typeof surgery?.lead === 'string' ? surgery?.lead : null) || urlLeadId || id;

  useEffect(() => {
    if (targetLeadId) {
      getCallLogs(targetLeadId, 50)
        .then(logs => {
          setCallLogs(logs || []);
          if (logs && logs.length > 0) setLatestCall(logs[0]);
          else setLatestCall(null);
        })
        .catch(() => {});
    }
  }, [targetLeadId, callHistoryRefresh]);

  const handleExotelCall = async () => {
    const phoneToCall = surgery?.lead?.patientPhone || surgery?.patientPhone;
    if (!phoneToCall) return toast.error('No patient phone number available.');
    if (!user?.phone) return toast.error('Your profile has no phone number. Update your profile to make calls.');
    setIsCallingPatient(true);
    try {
      const result = await exotelInitiateCall(targetLeadId, phoneToCall);
      if (result.success) {
        toast.success('📞 Your phone will ring first — then connect to the patient!', { duration: 6000 });
        setTimeout(() => setCallHistoryRefresh(prev => prev + 1), 5000);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to initiate call. Check Exotel configuration.');
    } finally {
      setIsCallingPatient(false);
    }
  };

  const handleOpenQuickDispModal = (logToUpdate = null) => {
    const log = logToUpdate || latestCall;
    if (!log) return;
    const eff = getEffectiveDisposition(log);
    setQuickDispForm({
      disposition: eff === 'pending' ? 'callback_completed' : eff,
      dispositionNotes: log.dispositionNotes || log.notes || '',
      callReason: log.callReason || ''
    });
    setShowQuickDispModal(true);
  };

  const handleSaveQuickDisp = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!latestCall?._id) return;
    setIsSavingQuickDisp(true);
    try {
      const res = await saveCallDisposition(latestCall._id, {
        disposition: quickDispForm.disposition,
        dispositionNotes: quickDispForm.dispositionNotes.trim(),
        notes: quickDispForm.dispositionNotes.trim(),
        callReason: quickDispForm.callReason.trim()
      });
      const updated = res?.data || {
        ...latestCall,
        disposition: quickDispForm.disposition,
        dispositionNotes: quickDispForm.dispositionNotes.trim(),
        notes: quickDispForm.dispositionNotes.trim(),
        callReason: quickDispForm.callReason.trim(),
        dispositionUpdatedAt: new Date().toISOString()
      };
      setLatestCall(updated);
      setCallLogs(prev => prev.map(l => l._id === latestCall._id ? { ...l, ...updated } : l));
      setCallHistoryRefresh(prev => prev + 1);
      toast.success('Follow-up status updated');
      setShowQuickDispModal(false);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to update status');
    } finally {
      setIsSavingQuickDisp(false);
    }
  };

  useEffect(() => {
    const active = detectPipeline(id, urlLeadId, surgery, searchParams);
    if (active) {
      setSelectedPipeline(active);
    }
  }, [id, urlLeadId, surgery, searchParams]);

  useEffect(() => {
    fetchSurgery();
  }, [id, urlLeadId]);


  const fetchSurgery = async () => {
    try {
      const leadId = urlLeadId || null;
      const surgeryId = leadId ? null : id;

      if (leadId) {
        const leadRes = await api.get(`/leads/${leadId}`);
        const leadData = leadRes.data.data;

        let surgeryData = null;
        try {
          const surgRes = await api.get(`/surgeries?leadId=${leadId}`);
          if (surgRes.data?.success && surgRes.data?.data?.surgeries?.length > 0) {
            const match = surgRes.data.data.surgeries.find(s =>
              (s.lead?._id || s.lead || '').toString() === id.toString()
            );
            if (match) surgeryData = match;
          }
        } catch (surgErr) {

        }

        let history = leadData.payments || surgeryData?.paymentHistory || [];

        if (!history || history.length === 0) {
          if (surgeryData?.paymentLinkDetails?.url) {
            history = [{
              _id: surgeryData.paymentLinkDetails.plinkId || id,
              amount: surgeryData.paymentLinkDetails.amount || 0,
              status: surgeryData.paymentLinkDetails.status || ((surgeryData.status === 'paid' || surgeryData.status === 'payment_completed' || leadData.status === 'paid' || leadData.status === 'payment_completed') ? 'completed' : 'pending'),
              razorpayOrderId: surgeryData.paymentLinkDetails.plinkId,
              razorpayPaymentId: surgeryData.paymentLinkDetails.plinkId,
              paymentLink: surgeryData.paymentLinkDetails.url,
              createdAt: surgeryData.updatedAt || leadData.updatedAt || new Date()
            }];
          } else if (leadData.status === 'paid' || leadData.status === 'payment_completed' || surgeryData?.status === 'payment_completed' || surgeryData?.status === 'paid') {
            const totalAmount = leadData.billingDetails?.totalPayableAmount || leadData.billingDetails?.procedureAmount || surgeryData?.surgeryCost || 0;
            history = [{
              _id: leadData.billingDetails?.transactionId || id,
              amount: totalAmount,
              status: 'completed',
              razorpayOrderId: leadData.billingDetails?.transactionId || `PAY_${id.slice(-8).toUpperCase()}`,
              razorpayPaymentId: leadData.billingDetails?.transactionId || `PAY_${id.slice(-8).toUpperCase()}`,
              paymentMethod: leadData.billingDetails?.paymentMethod || 'Direct / System Verified',
              createdAt: leadData.updatedAt || new Date()
            }];
          }
        }

        // Map Lead/Surgery fields to SurgeryDetail layout
        let appts = [];
        try {
          const apptRes = await api.get(`/appointments?leadId=${leadId}`);
          if (apptRes.data?.success && apptRes.data?.data?.appointments) {
            appts = apptRes.data.data.appointments;
          }
        } catch (e) { }

        setSurgery({
          _id: surgeryData?._id || '',
          bookingId: leadData.bookingId || leadData.leadId || leadId.slice(-8).toUpperCase(),
          lead: leadData,
          surgeon: leadData.assignedSurgeon || surgeryData?.surgeon,
          anesthesiologist: leadData.assignedAnesthesiologist || surgeryData?.anesthesiologist,
          hospital: leadData.assignedHospital || surgeryData?.hospital,
          surgeryName: leadData.disease || leadData.treatmentRequired || surgeryData?.surgeryName || 'Surgical Procedure',
          surgeryType: leadData.treatmentRequired || surgeryData?.surgeryType || 'TBD',
          scheduledDate: leadData.schedulingData?.surgeryDate || surgeryData?.scheduledDate || new Date(),
          scheduledTime: leadData.schedulingData?.otSlot || surgeryData?.scheduledTime || 'TBD',
          estimatedDuration: surgeryData?.estimatedDuration || 180,
          surgeryCost: leadData.billingDetails?.totalPayableAmount || surgeryData?.surgeryCost || 0,
          status: surgeryData?.status || leadData.status || 'new',
          paymentHistory: history,
          appointments: appts.length > 0 ? appts : (surgeryData?.appointments || []),
          preOperativeNotes: surgeryData?.preOperativeNotes || '',
          postOperativeNotes: surgeryData?.postOperativeNotes || '',
          complications: surgeryData?.complications || ''
        });

        const activeChoice = surgeryData?.pipelineChoice || leadData?.pipelineChoice;
        if (activeChoice) setSelectedPipeline(activeChoice);
        setHasActualSurgery(!!surgeryData);
      } else if (surgeryId) {
        try {
          const response = await api.get(`/surgeries/${surgeryId}`);
          const data = response.data.data;

          let appts = data.appointments || [];
          if (!appts || appts.length === 0) {
            const targetLeadId = data.lead?._id || data.lead;
            if (targetLeadId) {
              try {
                const apptRes = await api.get(`/appointments?leadId=${targetLeadId}`);
                if (apptRes.data?.success && apptRes.data?.data?.appointments) {
                  appts = apptRes.data.data.appointments;
                }
              } catch (e) { }
            }
          }

          setSurgery({
            ...data,
            appointments: appts
          });
          setHasActualSurgery(true);

          const activeChoice = data?.pipelineChoice || data?.lead?.pipelineChoice;
          if (activeChoice) setSelectedPipeline(activeChoice);
        } catch (surgErr) {
          // If not found in /surgeries/:id, check if it's a Lead ID
          try {
            const leadRes = await api.get(`/leads/${surgeryId}`);
            if (leadRes.data?.success && leadRes.data?.data) {
              const leadData = leadRes.data.data;
              let surgeryData = null;
              try {
                const sRes = await api.get(`/surgeries?leadId=${surgeryId}`);
                if (sRes.data?.success && sRes.data?.data?.surgeries?.length > 0) {
                  surgeryData = sRes.data.data.surgeries[0];
                }
              } catch (e) { }

              let history = leadData.payments || surgeryData?.paymentHistory || [];

              let appts = [];
              try {
                const apptRes = await api.get(`/appointments?leadId=${surgeryId}`);
                if (apptRes.data?.success && apptRes.data?.data?.appointments) {
                  appts = apptRes.data.data.appointments;
                }
              } catch (e) { }

              setSurgery({
                _id: surgeryData?._id || '',
                bookingId: leadData.bookingId || leadData.leadId || surgeryId.slice(-8).toUpperCase(),
                lead: leadData,
                surgeon: leadData.assignedSurgeon || surgeryData?.surgeon,
                anesthesiologist: leadData.assignedAnesthesiologist || surgeryData?.anesthesiologist,
                hospital: leadData.assignedHospital || surgeryData?.hospital,
                surgeryName: leadData.disease || leadData.treatmentRequired || surgeryData?.surgeryName || 'Surgical Procedure',
                surgeryType: leadData.treatmentRequired || surgeryData?.surgeryType || 'TBD',
                scheduledDate: leadData.schedulingData?.surgeryDate || surgeryData?.scheduledDate || new Date(),
                scheduledTime: leadData.schedulingData?.otSlot || surgeryData?.scheduledTime || 'TBD',
                estimatedDuration: surgeryData?.estimatedDuration || 180,
                surgeryCost: leadData.billingDetails?.totalPayableAmount || surgeryData?.surgeryCost || 0,
                status: surgeryData?.status || leadData.status || 'new',
                paymentHistory: history,
                appointments: appts.length > 0 ? appts : (surgeryData?.appointments || []),
                preOperativeNotes: surgeryData?.preOperativeNotes || '',
                postOperativeNotes: surgeryData?.postOperativeNotes || '',
                complications: surgeryData?.complications || ''
              });

              const activeChoice = surgeryData?.pipelineChoice || leadData?.pipelineChoice;
              if (activeChoice) setSelectedPipeline(activeChoice);
              setHasActualSurgery(!!surgeryData);
            } else {
              throw surgErr;
            }
          } catch (leadErr) {
            throw surgErr;
          }
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to load details');
      navigate('/surgeries');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPipeline = async (choice) => {
    setSelectedPipeline(choice);
    savePipelineChoice(choice, id, urlLeadId, surgery);

    const targetLeadId = surgery?.lead?._id || (typeof surgery?.lead === 'string' ? surgery?.lead : null) || urlLeadId || id;
    const surgId = surgery?._id;

    try {
      if (surgId) {
        await api.patch(`/surgeries/${surgId}`, { pipelineChoice: choice });
      }
      if (targetLeadId) {
        await api.patch(`/leads/${targetLeadId}`, {
          pipelineChoice: choice,
          leadType: choice === 'appointment' ? 'appointment' : (choice === 'ophe' ? 'ophe' : 'surgery')
        });
      }
    } catch (err) {
      console.warn('Pipeline update error:', err);
    }

    if (choice === 'appointment') {
      const existingApptId = surgery?.appointments?.[0]?._id;
      if (existingApptId) {
        navigate(`/appointments/${existingApptId}`);
      } else {
        try {
          const apptRes = await api.get(`/appointments?leadId=${targetLeadId}&limit=1`);
          if (apptRes.data?.data?.appointments?.[0]?._id) {
            navigate(`/appointments/${apptRes.data.data.appointments[0]._id}`);
            return;
          }
        } catch (e) { }
        navigate(`/appointments/create?leadId=${targetLeadId}&from=appointments&origin=appointments`);
      }
    } else if (choice === 'ophe') {
      toast.success('Assigned to OPHE Pipeline');
      let encId = targetLeadId;
      try {
        if (targetLeadId) {
          encId = await getEncryptedLeadId(targetLeadId);
        }
      } catch (e) {
        console.warn('Encryption fallback:', e);
      }
      if (encId) {
        navigate(`/ophe/leads/${encId}?from=surgeries&origin=surgeries&menu=surgeries`);
      } else {
        navigate('/ophe/pipeline');
      }
    } else {
      navigate(`/leads/${targetLeadId}?from=surgeries&origin=surgeries&menu=surgeries`);
    }
  };

  const handleConfirmOpheLead = async () => {
    setConfirmingOphe(true);
    try {
      const targetLeadId = surgery?.lead?._id || (typeof surgery?.lead === 'string' ? surgery?.lead : null) || urlLeadId || id;
      const surgId = surgery?._id;
      const currentStatus = surgery?.lead?.status || surgery?.status || 'new';
      const nextStatus = currentStatus === 'new' ? 'patient_ready' : currentStatus;

      if (targetLeadId) {
        await api.patch(`/leads/${targetLeadId}`, {
          pipelineChoice: 'ophe',
          leadType: 'ophe',
          ...(currentStatus === 'new' ? { status: nextStatus } : {})
        });
      }
      if (surgId) {
        try {
          await api.patch(`/surgeries/${surgId}`, {
            pipelineChoice: 'ophe',
            ...(currentStatus === 'new' ? { status: nextStatus } : {})
          });
        } catch (surgErr) {
          console.warn('Non-blocking surgery sync warning:', surgErr);
        }
      }
      savePipelineChoice('ophe', id, urlLeadId, surgery);
      toast.success('Opening OPHE Pipeline...');
      
      let encId = targetLeadId;
      try {
        if (targetLeadId) {
          encId = await getEncryptedLeadId(targetLeadId);
        }
      } catch (e) {
        console.warn('Encryption fallback:', e);
      }

      if (encId) {
        navigate(`/ophe/leads/${encId}?from=surgeries&origin=surgeries&menu=surgeries`);
      } else {
        navigate('/ophe/pipeline');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to confirm lead');
    } finally {
      setConfirmingOphe(false);
    }
  };

  const handleAcceptLead = async () => {
    if (isOphe) {
      return handleConfirmOpheLead();
    }
    setConfirmingOphe(true);
    try {
      const tid = surgery?.lead?._id || (typeof surgery?.lead === 'string' ? surgery?.lead : null) || urlLeadId || id;
      const surgId = surgery?._id;
      if (tid) {
        await api.patch(`/leads/${tid}`, {
          status: 'patient_ready'
        });
      }
      if (surgId) {
        try {
          await api.patch(`/surgeries/${surgId}`, {
            status: 'scheduled'
          });
        } catch (e) {
          console.warn('Sync notice:', e);
        }
      }
      toast.success('Lead Accepted! Patient marked ready for clinical care.');
      fetchSurgery();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to accept lead');
    } finally {
      setConfirmingOphe(false);
    }
  };

  const handleRejectLead = async () => {
    setIsRejecting(true);
    try {
      const tid = surgery?.lead?._id || (typeof surgery?.lead === 'string' ? surgery?.lead : null) || urlLeadId || id;
      const surgId = surgery?._id;
      const fullReason = rejectNotes.trim() ? `${rejectReason}: ${rejectNotes.trim()}` : rejectReason;

      if (tid) {
        await api.patch(`/leads/${tid}`, {
          status: 'not_interested',
          notInterestedReason: fullReason,
          leadStatusReason: fullReason
        });
      }
      if (surgId) {
        try {
          await api.patch(`/surgeries/${surgId}`, {
            status: 'cancelled',
            cancelReason: fullReason
          });
        } catch (surgErr) {
          console.warn('Non-blocking surgery cancel sync:', surgErr);
        }
      }
      toast.success('Lead rejected and moved to closed cases.');
      setShowRejectModal(false);
      setRejectNotes('');
      fetchSurgery();
    } catch (err) {
      console.error('Failed to reject lead:', err);
      toast.error(err.response?.data?.message || 'Failed to reject lead');
    } finally {
      setIsRejecting(false);
    }
  };

  const handleRestoreLead = async () => {
    setIsRestoringLead(true);
    try {
      const tid = surgery?.lead?._id || (typeof surgery?.lead === 'string' ? surgery?.lead : null) || urlLeadId || id;
      const surgId = surgery?._id;

      if (tid) {
        await api.patch(`/leads/${tid}`, {
          status: 'new',
          notInterestedReason: null,
          leadStatusReason: null
        });
      }
      if (surgId) {
        try {
          await api.patch(`/surgeries/${surgId}`, {
            status: 'scheduled'
          });
        } catch (e) {
          console.warn('Restore sync note:', e);
        }
      }
      toast.success('Lead reopened successfully and restored to active triage!');
      fetchSurgery();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to reopen lead');
    } finally {
      setIsRestoringLead(false);
    }
  };

  const handleStartSurgery = async () => {
    setUpdating(true);
    try {
      await api.post(`/surgeries/${id}/start`);
      toast.success('Surgery started successfully!');
      fetchSurgery();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to start surgery');
    } finally {
      setUpdating(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otpCode) {
      toast.error('Please enter OTP code');
      return;
    }

    setUpdating(true);
    try {
      await api.post(`/surgeries/${id}/verify-otp`, {
        otpCode: otpCode,
        otpId: surgery.surgeryStartOtp?.otpId?._id || surgery.surgeryStartOtp?.otpId
      });
      toast.success('OTP verified successfully!');
      setShowOTPModal(false);
      setOtpCode('');
      fetchSurgery();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to verify OTP');
    } finally {
      setUpdating(false);
    }
  };

  const handleCompleteSurgery = async () => {
    setUpdating(true);
    try {
      await api.post(`/surgeries/${id}/complete`, {
        postOperativeNotes,
        outcome
      });
      toast.success('Surgery completed successfully!');
      setShowCompleteModal(false);
      setPostOperativeNotes('');
      setOutcome('successful');
      fetchSurgery();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to complete surgery');
    } finally {
      setUpdating(false);
    }
  };

  const handleCancelSurgery = async () => {
    if (!window.confirm('Are you sure you want to cancel this surgery?')) {
      return;
    }

    setUpdating(true);
    try {
      await api.post(`/surgeries/${id}/cancel`, { reason: 'Cancelled by user' });
      toast.success('Surgery cancelled successfully!');
      fetchSurgery();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to cancel surgery');
    } finally {
      setUpdating(false);
    }
  };

  const copyBookingId = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedId(true);
    toast.success('Booking ID copied to clipboard!');
    setTimeout(() => setCopiedId(false), 2000);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    if (typeof date === 'string') {
      const trimmed = date.trim();
      if (/^\d{2}-\d{2}-\d{4}$/.test(trimmed)) return trimmed;
      const isoMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (isoMatch) return `${isoMatch[3]}-${isoMatch[2]}-${isoMatch[1]}`;
    }
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return 'N/A';
    const dd = String(d.getUTCDate()).padStart(2, '0');
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    const yyyy = d.getUTCFullYear();
    return `${dd}-${mm}-${yyyy}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-9 w-9 border-3 border-[#143E78] border-t-transparent"></div>
          <p className="text-xs font-bold text-slate-500 tracking-wide">Loading Patient Details...</p>
        </div>
      </div>
    );
  }

  if (!surgery) {
    return (
      <div className="text-center py-16 bg-white rounded-2xl border border-slate-200">
        <h2 className="text-base font-bold text-slate-800">Patient Case Not Found</h2>
        <button
          onClick={() => navigate('/surgeries')}
          className="mt-4 px-4 py-2 bg-[#143E78] text-white rounded-xl text-xs font-bold hover:bg-[#0f2e5a] transition-all"
        >
          Return to Pipeline
        </button>
      </div>
    );
  }

  const patientName = surgery.lead?.patientName || 'Patient Case';
  const bookingId = surgery.bookingId || surgery.lead?.bookingId || (surgery.lead?.leadId ? `#${surgery.lead.leadId}` : null);
  const patientPhone = surgery.lead?.patientPhone;
  const patientAge = surgery.lead?.patientAge;
  const patientGender = surgery.lead?.patientGender;
  const bloodGroup = surgery.lead?.bloodGroup;
  const fullAddress = [surgery.lead?.patientAddress, surgery.lead?.city].filter(Boolean).join(', ');
  const leadSource = surgery.lead?.leadGeneratedByType || 'INTERNAL INTAKE';
  const leadSourceName = surgery.lead?.leadGeneratedByName || 'Direct CRM Registration';
  const statusStr = (surgery.status || surgery.lead?.status || 'NEW').toUpperCase().replace(/_/g, ' ');
  const effectivePipeline = selectedPipeline || surgery.pipelineChoice || surgery.lead?.pipelineChoice;
  const isOphe = effectivePipeline
    ? effectivePipeline === 'ophe'
    : (surgery.lead?.leadType === 'ophe' || 
       surgery.lead?.source === 'ophe' || 
       surgery.lead?.leadGeneratedByType === 'ophe' || 
       surgery.source === 'ophe');

  const matchedOpheService = isOphe ? opheCatalog.find(s => {
    const leadServiceId = surgery.lead?.opheServiceDetails?.serviceId?._id || surgery.lead?.opheServiceDetails?.serviceId;
    if (leadServiceId && s._id.toString() === leadServiceId.toString()) return true;
    const req = (surgery.lead?.treatmentRequired || '').toLowerCase();
    const sName = (s.serviceName || '').toLowerCase();
    if (sName && req && (req.includes(sName) || sName.includes(req))) return true;
    const detailsName = (surgery.lead?.opheServiceDetails?.serviceName || '').toLowerCase();
    if (detailsName && sName && (detailsName.includes(sName) || sName.includes(detailsName))) return true;
    return false;
  }) : null;

  const ophePlanDuration = surgery.lead?.opheServiceDetails?.planDuration || 'perDay';
  const durationLabels = {
    perDay: 'Per Day',
    days7: '7 Days Package',
    days15: '15 Days Package',
    days30: '30 Days Package'
  };
  const opheDurationLabel = durationLabels[ophePlanDuration] || 'Per Day';
  const ophePrice = surgery.lead?.opheServiceDetails?.price || 
                    matchedOpheService?.pricing?.[ophePlanDuration] || 
                    matchedOpheService?.pricing?.perDay || 0;
  const opheServiceName = surgery.lead?.opheServiceDetails?.serviceName || 
                          matchedOpheService?.serviceName || 
                          surgery.lead?.treatmentRequired || 
                          'OPHE Care Service';
  const opheCategoryName = surgery.lead?.opheServiceDetails?.categoryName || 
                           matchedOpheService?.categoryId?.name || 
                           'Care Package';

  const currentLeadStatus = surgery?.lead?.status || surgery?.status || 'new';
  const isLeadRejected = currentLeadStatus === 'not_interested' || currentLeadStatus === 'cancelled';
  const patientInitials = (patientName || 'Patient Case')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(p => p[0]?.toUpperCase())
    .join('') || 'PT';

  return (
    <div className="space-y-5 pb-12 font-sans">
      {/* 🖨️ PRINT STYLES */}
      <style>
        {`
          @media print {
            .no-print { display: none !important; }
            html, body {
              background: white !important;
              margin: 0 !important;
              padding: 0 !important;
            }
          }
        `}
      </style>

      {/* 🖨️ PRINT HEADER */}
      <div className="hidden print:flex items-center justify-between border-b pb-4 mb-6">
        <img src={logo} alt="Doxez" className="h-10 w-auto" />
        <div className="text-right">
          <p className="text-xs font-bold text-slate-800">Doxez Hospital Network • Patient Medical Dossier</p>
          <p className="text-[10px] text-slate-500">{formatDate(new Date())}</p>
        </div>
      </div>

      {/* 🔝 BREADCRUMB & WORKSPACE ACTION BAR */}
      <div className="no-print flex flex-col md:flex-row md:items-center md:justify-between gap-4 pb-2">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2.5 text-slate-600 hover:text-slate-900 hover:bg-white bg-white border border-slate-200/90 rounded-2xl transition-all active:scale-95 cursor-pointer shadow-xs"
            title="Go Back"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <div className="flex items-center gap-2 text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-0.5">
              <span>Patient Dossier</span>
              <span>•</span>
              <span className="text-teal-700 font-extrabold font-mono">{bookingId || 'DOX-CASE'}</span>
            </div>
            <h1 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight flex items-center gap-2.5">
              <span>{patientName}</span>
              {isLeadRejected ? (
                <span className="px-2.5 py-0.5 bg-rose-50 text-rose-700 border border-rose-200 text-[11px] font-bold rounded-full">
                  Rejected / Closed
                </span>
              ) : currentLeadStatus === 'new' ? (
                <span className="px-2.5 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 text-[11px] font-bold rounded-full">
                  New Intake
                </span>
              ) : (
                <span className="px-2.5 py-0.5 bg-teal-50 text-teal-800 border border-teal-200 text-[11px] font-bold rounded-full">
                  {statusStr}
                </span>
              )}
            </h1>
          </div>
        </div>

        {/* 🔄 Action Toolbar (Accept, Reject, Pipeline Switch, Print) */}
        <div className="flex items-center flex-wrap gap-2.5">
          {/* ✅ Accept & Reject Group */}
          {isLeadRejected ? (
            <button
              onClick={handleRestoreLead}
              disabled={isRestoringLead}
              className="px-4 py-2.5 bg-indigo-50 hover:bg-indigo-100 text-[#143E78] border border-indigo-200 rounded-xl text-xs font-bold flex items-center gap-2 transition-all cursor-pointer shadow-xs active:scale-95 disabled:opacity-50"
            >
              <RotateCcw className={`w-3.5 h-3.5 ${isRestoringLead ? 'animate-spin' : ''}`} />
              <span>{isRestoringLead ? 'Reopening...' : 'Reopen / Restore Lead'}</span>
            </button>
          ) : (
            <>
              {/* ACCEPT BUTTON */}
              <button
                onClick={handleAcceptLead}
                disabled={confirmingOphe}
                className="px-4 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white rounded-xl text-xs font-black flex items-center gap-2 transition-all cursor-pointer shadow-md shadow-emerald-700/20 active:scale-95 disabled:opacity-50"
                title="Accept this patient lead and move to active care pipeline"
              >
                <CheckCircle2 className="w-4 h-4 text-emerald-100" />
                <span>
                  {confirmingOphe
                    ? 'Processing...'
                    : (currentLeadStatus !== 'new'
                      ? (isOphe ? 'OPHE Pipeline' : 'Accepted (Ready)')
                      : 'Accept Lead')}
                </span>
              </button>

              {/* REJECT BUTTON */}
              <button
                type="button"
                onClick={() => setShowRejectModal(true)}
                disabled={isRejecting}
                className="px-3.5 py-2.5 bg-white hover:bg-rose-50 text-rose-700 hover:text-rose-800 border border-rose-200/90 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer shadow-xs active:scale-95 disabled:opacity-50"
                title="Reject or decline this patient inquiry"
              >
                <XCircle className="w-4 h-4 text-rose-500" />
                <span>Reject Lead</span>
              </button>
            </>
          )}

          {/* Pipeline Conversion Segmented Group */}
          {isOphe ? (
            <div className="flex items-center bg-slate-100 p-1 rounded-xl border border-slate-200 text-xs font-bold gap-1">
              <button
                type="button"
                onClick={() => handleSelectPipeline('surgical')}
                title="Patient wants surgery instead of home care"
                className="px-2.5 py-1.5 text-slate-600 hover:text-emerald-700 hover:bg-white rounded-lg flex items-center gap-1.5 transition-all cursor-pointer"
              >
                <Activity className="w-3.5 h-3.5 text-emerald-600" />
                <span>Convert to DOXEZ Surgery</span>
              </button>
              <button
                type="button"
                onClick={() => handleSelectPipeline('appointment')}
                title="Patient wants doctor consultation instead of home care"
                className="px-2.5 py-1.5 text-slate-600 hover:text-[#143E78] hover:bg-white rounded-lg flex items-center gap-1.5 transition-all cursor-pointer"
              >
                <CalendarClock className="w-3.5 h-3.5 text-blue-600" />
                <span>Convert to Appointment</span>
              </button>
            </div>
          ) : (
            <div className="flex items-center bg-slate-100 p-1 rounded-xl border border-slate-200 text-xs font-bold gap-1">
              <button
                type="button"
                onClick={() => handleSelectPipeline('appointment')}
                className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition-all cursor-pointer ${
                  selectedPipeline === 'appointment'
                    ? 'bg-white text-[#143E78] shadow-2xs font-extrabold border border-slate-200/80'
                    : 'text-slate-600 hover:text-[#143E78] hover:bg-white/70'
                }`}
              >
                <CalendarClock className="w-3.5 h-3.5 text-blue-600" />
                <span>Appointment Pipeline</span>
              </button>

              <button
                type="button"
                onClick={() => handleSelectPipeline('surgical')}
                className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition-all cursor-pointer ${
                  selectedPipeline === 'surgical'
                    ? 'bg-white text-emerald-800 shadow-2xs font-extrabold border border-slate-200/80'
                    : 'text-slate-600 hover:text-emerald-700 hover:bg-white/70'
                }`}
              >
                <Activity className="w-3.5 h-3.5 text-emerald-600" />
                <span>Surgical Pipeline</span>
              </button>

              <button
                type="button"
                onClick={() => handleSelectPipeline('ophe')}
                className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition-all cursor-pointer ${
                  selectedPipeline === 'ophe'
                    ? 'bg-white text-teal-800 shadow-2xs font-extrabold border border-slate-200/80'
                    : 'text-slate-600 hover:text-teal-700 hover:bg-white/70'
                }`}
              >
                <Sparkles className="w-3.5 h-3.5 text-teal-600" />
                <span>OPHE Pipeline</span>
              </button>
            </div>
          )}

          <button
            onClick={() => window.print()}
            className="px-3.5 py-2.5 bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer shadow-xs"
            title="Print dossier summary"
          >
            <Download className="w-3.5 h-3.5 text-slate-500" />
            <span>Print Summary</span>
          </button>

          {/* Doctor Actions */}
          {user?.role === 'doctor' && surgery.status === 'scheduled' && !surgery.surgeryStartOtp?.isVerified && (
            <button
              onClick={() => setShowOTPModal(true)}
              className="px-3.5 py-2.5 bg-[#143E78] text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-sm hover:bg-[#0f2e5a] transition-all cursor-pointer"
            >
              <Key className="w-3.5 h-3.5" />
              <span>Verify OT OTP</span>
            </button>
          )}

          {user?.role === 'doctor' && surgery.status === 'scheduled' && surgery.surgeryStartOtp?.isVerified && (
            <button
              onClick={handleStartSurgery}
              disabled={updating}
              className="px-3.5 py-2.5 bg-emerald-700 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-sm hover:bg-emerald-800 disabled:opacity-50 transition-all cursor-pointer"
            >
              <Activity className="w-3.5 h-3.5" />
              <span>{updating ? 'Starting...' : 'Start Procedure'}</span>
            </button>
          )}

          {user?.role === 'doctor' && surgery.status === 'surgery_started' && (
            <button
              onClick={() => setShowCompleteModal(true)}
              className="px-3.5 py-2.5 bg-emerald-700 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-sm hover:bg-emerald-800 transition-all cursor-pointer"
            >
              <CheckCircle2 className="w-3.5 h-3.5" />
              <span>Complete Procedure</span>
            </button>
          )}

          {(user?.role === 'admin' || user?.role === 'customer_care' || user?.role === 'cc_head') &&
            (surgery.status === 'scheduled' || surgery.status === 'in_progress') && (
              <button
                onClick={handleCancelSurgery}
                disabled={updating}
                className="px-3.5 py-2.5 bg-rose-50 text-rose-700 border border-rose-200 rounded-xl text-xs font-bold hover:bg-rose-100 disabled:opacity-50 transition-all cursor-pointer"
              >
                Cancel Case
              </button>
            )}
        </div>
      </div>

      {/* 🏥 HOSPITAL PATIENT EMR BANNER (CLINICAL IDENTITY HEADER) */}
      <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden transition-all">
        {/* Top Clinical Accent Bar */}
        <div className="h-1.5 bg-gradient-to-r from-[#143E78] via-teal-600 to-emerald-500" />

        {/* Clinical Patient Header */}
        <div className="p-5 sm:p-6 bg-gradient-to-b from-slate-50/70 to-white flex flex-col lg:flex-row lg:items-center justify-between gap-5 border-b border-slate-100">
          <div className="flex items-start sm:items-center gap-4">
            {/* Clinical Avatar with Initials */}
            <div className="relative shrink-0">
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-tr from-[#143E78] to-indigo-900 text-white flex items-center justify-center font-black text-lg sm:text-xl shadow-md border-2 border-white">
                {patientInitials}
              </div>
              <span className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white ${
                isLeadRejected ? 'bg-rose-500' : currentLeadStatus === 'new' ? 'bg-amber-500 animate-pulse' : 'bg-emerald-500'
              }`} title={isLeadRejected ? 'Lead Rejected' : currentLeadStatus === 'new' ? 'Pending Intake' : 'Active'} />
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center gap-2.5 flex-wrap">
                <h2 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight">
                  {patientName}
                </h2>

                {bookingId && (
                  <button
                    onClick={() => copyBookingId(bookingId)}
                    className="px-2.5 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-mono text-xs font-bold rounded-lg border border-slate-200 inline-flex items-center gap-1.5 transition-colors cursor-pointer"
                    title="Click to copy ID"
                  >
                    <span>{bookingId}</span>
                    {copiedId ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3 text-slate-400" />}
                  </button>
                )}

                <span className={`px-2.5 py-0.5 text-[11px] font-bold rounded-full uppercase border ${
                  isLeadRejected
                    ? 'bg-rose-50 text-rose-700 border-rose-200'
                    : currentLeadStatus === 'new'
                    ? 'bg-amber-50 text-amber-800 border-amber-200'
                    : 'bg-teal-50 text-teal-800 border-teal-200'
                }`}>
                  {isLeadRejected ? 'Rejected' : statusStr}
                </span>
              </div>

              <div className="flex items-center gap-3 text-xs text-slate-500 font-medium flex-wrap">
                <span className="flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5 text-slate-400" />
                  Intake: <strong className="text-slate-700 font-semibold">{formatDate(surgery.lead?.createdAt || surgery.scheduledDate)}</strong>
                </span>
                <span>•</span>
                <span className="flex items-center gap-1">
                  <Globe className="w-3.5 h-3.5 text-slate-400" />
                  Source: <strong className="text-slate-700 font-semibold">{leadSource} ({leadSourceName})</strong>
                </span>
              </div>
            </div>
          </div>

          {/* Quick Actions & Telephony Row */}
          <div className="flex items-center gap-2.5 flex-wrap self-start lg:self-auto">
            {/* Click-to-call patient via Exotel */}
            {patientPhone && (
              <button
                type="button"
                onClick={handleExotelCall}
                disabled={isCallingPatient}
                title="Click-to-Call patient via Exotel"
                className="px-3.5 py-2 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 text-emerald-800 rounded-xl text-xs font-bold flex items-center gap-2 transition-all cursor-pointer disabled:opacity-50 shadow-2xs active:scale-95"
              >
                {isCallingPatient ? (
                  <><Loader2 className="w-3.5 h-3.5 animate-spin text-emerald-600" /> <span>Calling…</span></>
                ) : (
                  <><PhoneCall className="w-3.5 h-3.5 text-emerald-600" /> <span>{patientPhone}</span></>
                )}
              </button>
            )}

            {/* Edit Patient Details Button */}
            {canEditPatient && (
              <button
                type="button"
                onClick={() => setShowEditPatientModal(true)}
                className="px-3.5 py-2 bg-[#143E78] hover:bg-[#0f2e5a] text-white rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all shadow-xs cursor-pointer active:scale-95"
                title="Edit Patient Details"
              >
                <Edit className="w-3.5 h-3.5" />
                <span>Edit Patient</span>
              </button>
            )}

            {/* Call Status & Follow-up Badges */}
            {latestCall && (
              <div className="flex items-center gap-2 flex-wrap">
                <span
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold border shadow-2xs ${getLightStatusBadge(latestCall.status).bg}`}
                  title="Call Status (Exotel Telephony)"
                >
                  <span className={`w-2 h-2 rounded-full ${getLightStatusBadge(latestCall.status).dot}`} />
                  {getLightStatusBadge(latestCall.status).label}
                </span>

                <button
                  type="button"
                  onClick={() => handleOpenQuickDispModal()}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold border transition-all hover:shadow-xs cursor-pointer shadow-2xs ${getLightDispositionBadge(getEffectiveDisposition(latestCall)).bg}`}
                  title="Click to update follow-up status"
                >
                  <span className={`w-2 h-2 rounded-full ${getLightDispositionBadge(getEffectiveDisposition(latestCall)).dot}`} />
                  <span>{getLightDispositionBadge(getEffectiveDisposition(latestCall)).label}</span>
                  <Edit className="w-3 h-3 ml-0.5 opacity-60" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Clinical Vitals & Demographics Ribbon */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5 p-3.5 bg-slate-50/80">
          <div className="p-3 bg-white rounded-xl border border-slate-200/80 shadow-2xs flex flex-col justify-between">
            <div className="flex items-center gap-1.5 mb-1.5">
              <div className="w-5 h-5 rounded-md bg-blue-50 text-[#143E78] flex items-center justify-center">
                <User className="w-3 h-3" />
              </div>
              <span className="text-[10px] font-black text-slate-500 uppercase tracking-wider">Age / Gender</span>
            </div>
            <span className="text-xs font-black text-slate-900 truncate">
              {patientAge ? `${patientAge} Yrs` : '—'} / {patientGender ? patientGender.charAt(0).toUpperCase() + patientGender.slice(1) : '—'}
            </span>
          </div>

          <div className="p-3 bg-white rounded-xl border border-slate-200/80 shadow-2xs flex flex-col justify-between">
            <div className="flex items-center gap-1.5 mb-1.5">
              <div className="w-5 h-5 rounded-md bg-rose-50 text-rose-600 flex items-center justify-center">
                <Droplet className="w-3 h-3" />
              </div>
              <span className="text-[10px] font-black text-slate-500 uppercase tracking-wider">Blood Group</span>
            </div>
            <span className={`text-xs font-black truncate ${bloodGroup ? 'text-rose-600' : 'text-slate-400 italic'}`}>
              {bloodGroup || 'Not Recorded'}
            </span>
          </div>

          {isOphe ? (
            <>
              <div className="p-3 bg-white rounded-xl border border-teal-200/80 shadow-2xs flex flex-col justify-between">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <div className="w-5 h-5 rounded-md bg-teal-50 text-teal-700 flex items-center justify-center">
                    <HeartHandshake className="w-3 h-3" />
                  </div>
                  <span className="text-[10px] font-black text-teal-800 uppercase tracking-wider">OPHE Service</span>
                </div>
                <span className="text-xs font-black text-teal-900 truncate uppercase" title={opheServiceName}>
                  {opheServiceName}
                </span>
              </div>

              <div className="p-3 bg-white rounded-xl border border-slate-200/80 shadow-2xs flex flex-col justify-between">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <div className="w-5 h-5 rounded-md bg-indigo-50 text-indigo-700 flex items-center justify-center">
                    <Clock className="w-3 h-3" />
                  </div>
                  <span className="text-[10px] font-black text-slate-500 uppercase tracking-wider">Care Duration</span>
                </div>
                <span className="text-xs font-black text-slate-900 truncate">
                  {opheDurationLabel}
                </span>
              </div>

              <div className="p-3 bg-white rounded-xl border border-emerald-200/80 shadow-2xs flex flex-col justify-between">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <div className="w-5 h-5 rounded-md bg-emerald-50 text-emerald-700 flex items-center justify-center">
                    <IndianRupee className="w-3 h-3" />
                  </div>
                  <span className="text-[10px] font-black text-emerald-800 uppercase tracking-wider">Package Price</span>
                </div>
                <span className="text-xs font-black text-emerald-700">
                  ₹{Number(ophePrice).toLocaleString('en-IN')}
                </span>
              </div>
            </>
          ) : (
            <>
              <div className="p-3 bg-white rounded-xl border border-slate-200/80 shadow-2xs flex flex-col justify-between">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <div className="w-5 h-5 rounded-md bg-teal-50 text-teal-700 flex items-center justify-center">
                    <Activity className="w-3 h-3" />
                  </div>
                  <span className="text-[10px] font-black text-slate-500 uppercase tracking-wider">Procedure</span>
                </div>
                <span className="text-xs font-black text-slate-900 truncate" title={surgery.lead?.treatmentRequired || surgery.surgeryName}>
                  {surgery.lead?.treatmentRequired || surgery.surgeryName || 'TBD'}
                </span>
              </div>

              <div className="p-3 bg-white rounded-xl border border-slate-200/80 shadow-2xs flex flex-col justify-between">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <div className="w-5 h-5 rounded-md bg-blue-50 text-[#143E78] flex items-center justify-center">
                    <Stethoscope className="w-3 h-3" />
                  </div>
                  <span className="text-[10px] font-black text-slate-500 uppercase tracking-wider">Surgeon</span>
                </div>
                <span className="text-xs font-black text-[#143E78] truncate">
                  {surgery.surgeon?.user ? `Dr. ${surgery.surgeon.user.firstName} ${surgery.surgeon.user.lastName || ''}` : 'Unassigned'}
                </span>
              </div>

              <div className="p-3 bg-white rounded-xl border border-slate-200/80 shadow-2xs flex flex-col justify-between">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <div className="w-5 h-5 rounded-md bg-purple-50 text-purple-700 flex items-center justify-center">
                    <Building2 className="w-3 h-3" />
                  </div>
                  <span className="text-[10px] font-black text-slate-500 uppercase tracking-wider">Hospital</span>
                </div>
                <span className="text-xs font-black text-slate-900 truncate">
                  {surgery.hospital?.hospitalName || 'Unassigned'}
                </span>
              </div>
            </>
          )}

          <div className="p-3 bg-white rounded-xl border border-slate-200/80 shadow-2xs flex flex-col justify-between">
            <div className="flex items-center gap-1.5 mb-1.5">
              <div className="w-5 h-5 rounded-md bg-amber-50 text-amber-700 flex items-center justify-center">
                <MapPin className="w-3 h-3" />
              </div>
              <span className="text-[10px] font-black text-slate-500 uppercase tracking-wider">City / State</span>
            </div>
            <span className="text-xs font-black text-slate-800 truncate">
              {surgery.lead?.city || (surgery.lead?.patientAddress ? surgery.lead.patientAddress.split(',').pop() : '—')}
            </span>
          </div>
        </div>
      </div>

      {/* 📑 CLINICAL SECTION TABS */}
      <div className="flex items-center p-1.5 bg-slate-200/70 rounded-2xl border border-slate-200 gap-1.5 overflow-x-auto no-scrollbar">
        {(isOphe ? [
          { id: 'clinical', label: 'OPHE Care & Health Assessment', icon: HeartHandshake },
          { id: 'calls', label: `Call History`, count: callLogs.length, icon: PhoneCall },
          { id: 'billing', label: 'Billing & Payments', icon: Wallet },
          { id: 'documents', label: `Documents`, count: surgery.lead?.documents?.length || 0, icon: FileText },
          { id: 'notes', label: 'Care Notes', icon: ClipboardList }
        ] : [
          { id: 'clinical', label: 'Clinical Assessment & Complaints', icon: HeartPulse },
          { id: 'calls', label: `Call History`, count: callLogs.length, icon: PhoneCall },
          { id: 'consultations', label: `Doctor Consultations`, count: surgery.appointments?.length || 0, icon: Calendar },
          { id: 'team', label: 'Care Team & Facility', icon: Stethoscope },
          { id: 'billing', label: 'Billing & Payments', icon: Wallet },
          { id: 'documents', label: `Documents`, count: surgery.lead?.documents?.length || 0, icon: FileText },
          { id: 'notes', label: 'Clinical Notes', icon: ClipboardList }
        ]).map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 py-2.5 px-4 text-xs font-bold rounded-xl transition-all cursor-pointer whitespace-nowrap ${
                isActive
                  ? 'bg-white text-[#143E78] shadow-sm font-extrabold border border-slate-200/80'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-white/60'
              }`}
            >
              <Icon className={`w-4 h-4 ${isActive ? 'text-[#143E78]' : 'text-slate-400'}`} />
              <span>{tab.label}</span>
              {typeof tab.count === 'number' && (
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-black ${
                  isActive ? 'bg-blue-50 text-[#143E78]' : 'bg-slate-300/60 text-slate-700'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* 🏥 TAB 1: CLINICAL ASSESSMENT */}
      {activeTab === 'clinical' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Main Clinical Assessment */}
          <div className="lg:col-span-2 space-y-5">
            {/* Primary Diagnosis & Treatment Card / OPHE Assessment Card */}
            {isOphe ? (
              <>
                {/* 🌟 OPHE SELECTED SERVICE & CARE PACKAGE CARD */}
                <div className="bg-white rounded-3xl border border-slate-200/90 p-5 sm:p-6 shadow-xs space-y-5 animate-fadeIn">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-3.5 border-b border-slate-100 gap-3">
                    <div className="border-l-4 border-teal-600 pl-3 py-0.5">
                      <div className="flex items-center gap-2">
                        <h3 className="text-base font-black text-slate-900">Selected OPHE Care Service & Package</h3>
                        <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-teal-50 text-teal-800 border border-teal-200 shadow-2xs">
                          {opheCategoryName}
                        </span>
                      </div>
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mt-0.5 block">
                        Assigned Care Plan & Package Inclusions
                      </span>
                    </div>

                    <div className="flex items-center gap-2 self-start sm:self-auto">
                      <span className="px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-700 shadow-2xs flex items-center gap-1.5">
                        <Clock className="w-3.5 h-3.5 text-teal-600" />
                        {opheDurationLabel}
                      </span>
                      <span className="px-3 py-1.5 bg-emerald-600 text-white rounded-xl text-xs font-black shadow-xs flex items-center gap-1">
                        <IndianRupee className="w-3.5 h-3.5" />
                        {Number(ophePrice).toLocaleString('en-IN')}
                      </span>
                    </div>
                  </div>

                  {/* Main Service Presentation */}
                  <div className="flex flex-col md:flex-row items-start md:items-center gap-4 p-4.5 bg-slate-50/70 rounded-2xl border border-slate-200/80 shadow-2xs">
                    {matchedOpheService?.imageUrl ? (
                      <img
                        src={matchedOpheService.imageUrl}
                        alt={opheServiceName}
                        className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl object-cover shrink-0 border border-teal-100 shadow-xs"
                      />
                    ) : (
                      <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-teal-600 text-white flex items-center justify-center shrink-0 shadow-xs">
                        <HeartHandshake className="w-10 h-10" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="px-2 py-0.5 bg-teal-100 text-teal-800 rounded-md text-[10px] font-black uppercase tracking-wider">
                          {opheCategoryName}
                        </span>
                        <span className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded-md text-[10px] font-black uppercase tracking-wider">
                          Plan: {opheDurationLabel}
                        </span>
                      </div>
                      <h4 className="text-lg font-black text-slate-900 leading-tight">{opheServiceName}</h4>
                      {matchedOpheService?.description && (
                        <p className="text-xs text-slate-600 mt-1 leading-relaxed">{matchedOpheService.description}</p>
                      )}
                    </div>
                  </div>

                  {/* Medical & Non-Medical Features Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Medical Coverage */}
                    <div className="p-4 bg-white rounded-xl border border-teal-100/80 space-y-2">
                      <div className="flex items-center gap-2 pb-1.5 border-b border-slate-100">
                        <Activity className="w-4 h-4 text-teal-600" />
                        <h5 className="text-xs font-black text-slate-800 uppercase tracking-wider">Medical Support Points</h5>
                      </div>
                      {matchedOpheService?.medicalPoints?.length > 0 ? (
                        <ul className="space-y-1.5 pt-1">
                          {matchedOpheService.medicalPoints.map((pt, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-xs font-semibold text-slate-700">
                              <CheckCircle2 className="w-3.5 h-3.5 text-teal-600 shrink-0 mt-0.5" />
                              <span>{pt}</span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-xs text-slate-400 italic pt-1">Standard clinical protocol assistance</p>
                      )}
                    </div>

                    {/* Non-Medical Coverage */}
                    <div className="p-4 bg-white rounded-xl border border-slate-200/80 space-y-2">
                      <div className="flex items-center gap-2 pb-1.5 border-b border-slate-100">
                        <Shield className="w-4 h-4 text-indigo-600" />
                        <h5 className="text-xs font-black text-slate-800 uppercase tracking-wider">Non-Medical / Caregiver Support</h5>
                      </div>
                      {matchedOpheService?.nonMedicalPoints?.length > 0 ? (
                        <ul className="space-y-1.5 pt-1">
                          {matchedOpheService.nonMedicalPoints.map((pt, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-xs font-semibold text-slate-700">
                              <CheckCircle2 className="w-3.5 h-3.5 text-indigo-600 shrink-0 mt-0.5" />
                              <span>{pt}</span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-xs text-slate-400 italic pt-1">Daily living and companionship support</p>
                      )}
                    </div>
                  </div>

                  {/* Pricing Breakdown Card */}
                  <div className="p-4 bg-white/90 rounded-2xl border border-teal-100 shadow-2xs space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-black uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
                        <IndianRupee className="w-3.5 h-3.5 text-teal-600" />
                        Full Standard Pricing & Shift Tiers
                      </span>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-teal-50 text-teal-800 border border-teal-200/60 capitalize">
                        {matchedOpheService?.pricing?.pricingMode === 'hourly_shift' ? 'Hourly / Shift' : matchedOpheService?.pricing?.pricingMode === 'per_day' ? 'Per Day Plan' : 'Daily & Shift'}
                      </span>
                    </div>

                    {/* Shift Rates */}
                    {(matchedOpheService?.pricing?.hours12 || matchedOpheService?.pricing?.hours24 || matchedOpheService?.pricing?.pricingMode === 'hourly_shift') && (
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div className="bg-slate-50 p-2 rounded-xl border border-teal-200/70">
                          <span className="text-[10px] text-teal-700 font-bold block">12h Shift Rate</span>
                          <span className="text-xs font-black text-slate-900">
                            ₹{(matchedOpheService?.pricing?.hours12 || 0).toLocaleString('en-IN')}
                          </span>
                        </div>
                        <div className="bg-slate-50 p-2 rounded-xl border border-indigo-200/70">
                          <span className="text-[10px] text-indigo-700 font-bold block">24h Shift Rate</span>
                          <span className="text-xs font-black text-indigo-950">
                            ₹{(matchedOpheService?.pricing?.hours24 || 0).toLocaleString('en-IN')}
                          </span>
                        </div>
                        <div className="bg-slate-50 p-2 rounded-xl border border-slate-200/80">
                          <span className="text-[10px] text-slate-500 font-bold block">Custom Shift / Hr</span>
                          <span className="text-xs font-black text-slate-800">
                            ₹{(matchedOpheService?.pricing?.customHoursRate || 0).toLocaleString('en-IN')}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Multi-Day Rates */}
                    {(matchedOpheService?.pricing?.perDay || matchedOpheService?.pricing?.days7 || matchedOpheService?.pricing?.pricingMode !== 'hourly_shift') && (
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
                        <div className={`p-2 rounded-xl border ${ophePlanDuration === 'perDay' ? 'bg-teal-50 border-teal-500 ring-1 ring-teal-500/20' : 'bg-slate-50 border-slate-200'}`}>
                          <span className="text-[10px] text-slate-500 font-bold block">Per Day</span>
                          <span className={`text-xs font-black ${ophePlanDuration === 'perDay' ? 'text-teal-900' : 'text-slate-900'}`}>
                            ₹{(matchedOpheService?.pricing?.perDay || 0).toLocaleString('en-IN')}
                          </span>
                        </div>
                        <div className={`p-2 rounded-xl border ${ophePlanDuration === 'days7' ? 'bg-teal-50 border-teal-500 ring-1 ring-teal-500/20' : 'bg-slate-50 border-slate-200'}`}>
                          <span className="text-[10px] text-slate-500 font-bold block">7 Days</span>
                          <span className={`text-xs font-black ${ophePlanDuration === 'days7' ? 'text-teal-900' : 'text-teal-800'}`}>
                            ₹{(matchedOpheService?.pricing?.days7 || 0).toLocaleString('en-IN')}
                          </span>
                        </div>
                        <div className={`p-2 rounded-xl border ${ophePlanDuration === 'days15' ? 'bg-teal-50 border-teal-500 ring-1 ring-teal-500/20' : 'bg-slate-50 border-slate-200'}`}>
                          <span className="text-[10px] text-slate-500 font-bold block">15 Days</span>
                          <span className={`text-xs font-black ${ophePlanDuration === 'days15' ? 'text-teal-900' : 'text-blue-800'}`}>
                            ₹{(matchedOpheService?.pricing?.days15 || 0).toLocaleString('en-IN')}
                          </span>
                        </div>
                        <div className={`p-2 rounded-xl border ${ophePlanDuration === 'days30' ? 'bg-teal-50 border-teal-500 ring-1 ring-teal-500/20' : 'bg-slate-50 border-slate-200'}`}>
                          <span className="text-[10px] text-slate-500 font-bold block">30 Days</span>
                          <span className={`text-xs font-black ${ophePlanDuration === 'days30' ? 'text-teal-900' : 'text-emerald-800'}`}>
                            ₹{(matchedOpheService?.pricing?.days30 || 0).toLocaleString('en-IN')}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Custom Notes / Care Instructions */}
                  {surgery.lead?.opheServiceDetails?.customNotes && (
                    <div className="p-3.5 bg-teal-50/60 rounded-xl border border-teal-200/70 text-xs">
                      <span className="font-black text-teal-900 block mb-0.5 uppercase tracking-wider text-[10px]">Specific Care Instructions:</span>
                      <p className="text-slate-700 font-medium">{surgery.lead.opheServiceDetails.customNotes}</p>
                    </div>
                  )}
                </div>

                <div className="bg-white rounded-2xl border border-teal-200/80 p-5 sm:p-6 shadow-2xs space-y-5 animate-fadeIn">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-3 border-b border-teal-100 gap-2">
                    <div className="border-l-4 border-teal-600 pl-3 py-0.5">
                      <div className="flex items-center gap-2">
                        <h3 className="text-base font-black text-slate-900">OPHE Clinical & Medical Assessment</h3>
                        <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-teal-100 text-teal-800">
                          OPHE Case Details
                        </span>
                      </div>
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mt-0.5 block">
                        Complete Patient Health History, Vitals & Clinical Parameters
                      </span>
                    </div>
                  </div>

                  {/* OPHE Medical Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
                    {/* Diabetes / Sugar */}
                    <div className="p-4 bg-slate-50/80 rounded-xl border border-slate-200/80 space-y-1.5">
                      <div className="flex items-center justify-between">
                        <span className="text-[11px] font-black uppercase tracking-wider text-slate-600 flex items-center gap-1.5">
                          <Droplet className="w-3.5 h-3.5 text-rose-500" />
                          Diabetes / Sugar
                        </span>
                        <span className={`px-2.5 py-0.5 rounded-md text-[10px] font-black uppercase ${
                          surgery.lead?.opheMedicalInfo?.diabetesStatus === 'Yes'
                            ? 'bg-rose-100 text-rose-700 border border-rose-200'
                            : surgery.lead?.opheMedicalInfo?.diabetesStatus === 'No'
                              ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                              : 'bg-slate-200 text-slate-600'
                        }`}>
                          {surgery.lead?.opheMedicalInfo?.diabetesStatus || 'Not Recorded'}
                        </span>
                      </div>
                      {surgery.lead?.opheMedicalInfo?.diabetesSugarValue ? (
                        <p className="text-xs font-bold text-slate-700 pt-1">
                          Value: {surgery.lead.opheMedicalInfo.diabetesSugarValue}
                        </p>
                      ) : (
                        <p className="text-[11px] text-slate-400">No sugar readings entered</p>
                      )}
                    </div>

                    {/* Blood Pressure (BP) */}
                    <div className="p-4 bg-slate-50/80 rounded-xl border border-slate-200/80 space-y-1.5">
                      <div className="flex items-center justify-between">
                        <span className="text-[11px] font-black uppercase tracking-wider text-slate-600 flex items-center gap-1.5">
                          <Activity className="w-3.5 h-3.5 text-blue-500" />
                          Blood Pressure (BP)
                        </span>
                        <span className={`px-2.5 py-0.5 rounded-md text-[10px] font-black uppercase ${
                          surgery.lead?.opheMedicalInfo?.bpStatus === 'Yes'
                            ? 'bg-blue-100 text-blue-700 border border-blue-200'
                            : surgery.lead?.opheMedicalInfo?.bpStatus === 'No'
                              ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                              : 'bg-slate-200 text-slate-600'
                        }`}>
                          {surgery.lead?.opheMedicalInfo?.bpStatus || 'Not Recorded'}
                        </span>
                      </div>
                      {(surgery.lead?.opheMedicalInfo?.bpSystolic || surgery.lead?.opheMedicalInfo?.bpDiastolic) ? (
                        <p className="text-xs font-bold text-slate-700 pt-1">
                          BP: {surgery.lead.opheMedicalInfo.bpSystolic || '—'} / {surgery.lead.opheMedicalInfo.bpDiastolic || '—'} mmHg
                        </p>
                      ) : (
                        <p className="text-[11px] text-slate-400">No BP readings entered</p>
                      )}
                    </div>

                    {/* Thyroid */}
                    <div className="p-4 bg-slate-50/80 rounded-xl border border-slate-200/80 space-y-1.5">
                      <div className="flex items-center justify-between">
                        <span className="text-[11px] font-black uppercase tracking-wider text-slate-600 flex items-center gap-1.5">
                          <span className="w-2 h-2 rounded-full bg-violet-500"></span>
                          Thyroid
                        </span>
                        <span className={`px-2.5 py-0.5 rounded-md text-[10px] font-black uppercase ${
                          surgery.lead?.opheMedicalInfo?.thyroidStatus === 'Yes'
                            ? 'bg-violet-100 text-violet-700 border border-violet-200'
                            : surgery.lead?.opheMedicalInfo?.thyroidStatus === 'No'
                              ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                              : 'bg-slate-200 text-slate-600'
                        }`}>
                          {surgery.lead?.opheMedicalInfo?.thyroidStatus || 'Not Recorded'}
                        </span>
                      </div>
                      {surgery.lead?.opheMedicalInfo?.thyroidType && (
                        <p className="text-xs font-bold text-violet-800">
                          Type: {surgery.lead.opheMedicalInfo.thyroidType}
                        </p>
                      )}
                      {surgery.lead?.opheMedicalInfo?.thyroidValueReport ? (
                        <p className="text-[11px] font-medium text-slate-600">
                          Details: {surgery.lead.opheMedicalInfo.thyroidValueReport}
                        </p>
                      ) : (
                        <p className="text-[11px] text-slate-400">No thyroid notes entered</p>
                      )}
                    </div>
                  </div>

                  {/* Other Conditions & Current Medication */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="p-4 bg-slate-50/80 rounded-xl border border-slate-200/80 space-y-1.5">
                      <span className="text-[11px] font-black uppercase tracking-wider text-slate-600 flex items-center gap-1.5">
                        <AlertCircle className="w-3.5 h-3.5 text-amber-500" />
                        Other Medical Conditions
                      </span>
                      <p className="text-xs font-semibold text-slate-700 leading-relaxed">
                        {surgery.lead?.opheMedicalInfo?.otherConditions || 'No other chronic medical conditions specified.'}
                      </p>
                    </div>

                    <div className="p-4 bg-slate-50/80 rounded-xl border border-slate-200/80 space-y-1.5">
                      <span className="text-[11px] font-black uppercase tracking-wider text-slate-600 flex items-center gap-1.5">
                        <Pill className="w-3.5 h-3.5 text-emerald-500" />
                        Current Medication
                      </span>
                      <p className="text-xs font-semibold text-slate-700 leading-relaxed">
                        {surgery.lead?.opheMedicalInfo?.currentMedication || 'No current medication reported.'}
                      </p>
                    </div>
                  </div>

                  {/* Patient Address & Location */}
                  <div className="p-4 bg-slate-50/80 rounded-xl border border-slate-200/80 space-y-1">
                    <span className="text-[11px] font-black uppercase tracking-wider text-slate-600 flex items-center gap-1.5">
                      <MapPin className="w-3.5 h-3.5 text-[#143E78]" />
                      Complete Patient Address & Location
                    </span>
                    <p className="text-xs font-bold text-slate-800">
                      {[surgery.lead?.patientAddress, surgery.lead?.city, surgery.lead?.state, surgery.lead?.pincode].filter(Boolean).join(', ') || 'No detailed address recorded.'}
                    </p>
                  </div>

                  {/* Remarks / Clinical Notes if recorded */}
                  {(surgery.lead?.comments || surgery.lead?.remarks || surgery.lead?.diseaseDescription) && (
                    <div className="p-4 bg-slate-50/80 rounded-xl border border-slate-200/80 space-y-1">
                      <span className="text-[11px] font-black uppercase tracking-wider text-slate-600 flex items-center gap-1.5">
                        <FileText className="w-3.5 h-3.5 text-teal-600" />
                        Intake Remarks & Notes
                      </span>
                      <p className="text-xs text-slate-700 leading-relaxed font-medium">
                        {surgery.lead?.comments || surgery.lead?.remarks || surgery.lead?.diseaseDescription}
                      </p>
                    </div>
                  )}
                </div>

                {/* Doctor Consultation Info Preview for OPHE if appointments exist */}
                {surgery.appointments && surgery.appointments.length > 0 && (
                  <div className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-2xs space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                        <Stethoscope className="w-3.5 h-3.5 text-teal-600" /> Doctor Consultations ({surgery.appointments.length})
                      </span>
                      <button
                        type="button"
                        onClick={() => setActiveTab('consultations')}
                        className="text-xs font-bold text-[#143E78] hover:underline cursor-pointer flex items-center gap-1"
                      >
                        <span>View All</span>
                        <ChevronRight className="w-3 h-3" />
                      </button>
                    </div>

                    <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-100 flex items-center justify-between">
                      <div>
                        <span className="font-bold text-xs text-slate-900 block">
                          {surgery.appointments[0].appointmentDoctorId?.user
                            ? `Dr. ${surgery.appointments[0].appointmentDoctorId.user.firstName} ${surgery.appointments[0].appointmentDoctorId.user.lastName || ''}`
                            : (surgery.appointments[0].doctorName || 'Assigned Consultant')}
                        </span>
                        <span className="text-[11px] text-slate-500">
                          {surgery.appointments[0].appointmentDoctorId?.specialization || 'Consultation'} • {formatDate(surgery.appointments[0].appointmentDate || surgery.appointments[0].date)}
                        </span>
                      </div>
                      <span className="px-2.5 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full text-[10px] font-bold uppercase">
                        {surgery.appointments[0].status || 'Scheduled'}
                      </span>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-2xs space-y-4">
                <div className="border-l-4 border-[#143E78] pl-3 py-0.5">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Planned Medical Procedure</span>
                  <h3 className="text-base font-bold text-slate-900 mt-0.5">
                    {surgery.lead?.treatmentRequired || surgery.surgeryName || 'Surgical Assessment'}
                  </h3>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                  <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-100">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Diagnosed Condition</span>
                    <p className="text-xs font-bold text-slate-800">{surgery.lead?.disease || 'Under Evaluation'}</p>
                  </div>
                  <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-100">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Procedure Category</span>
                    <p className="text-xs font-bold text-slate-800">{surgery.surgeryType || 'Standard Surgical'}</p>
                  </div>
                </div>

                {/* Chief Complaints */}
                <div className="space-y-1.5 pt-2">
                  <span className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                    <AlertCircle className="w-3.5 h-3.5 text-amber-500" /> Presenting Chief Complaints
                  </span>
                  <div className="p-3 bg-slate-50 rounded-xl border border-slate-100 text-xs text-slate-700 leading-relaxed font-medium">
                    {surgery.lead?.diseaseDescription || surgery.lead?.disease || 'No acute complaints recorded during registration.'}
                  </div>
                </div>

                {/* Clinical Notes / Doctor Evaluation if present */}
                {surgery.preOperativeNotes && (
                  <div className="space-y-1.5 pt-2">
                    <span className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                      <FileText className="w-3.5 h-3.5 text-blue-500" /> Pre-Operative Clinical Findings
                    </span>
                    <div className="p-3 bg-blue-50/50 rounded-xl border border-blue-100 text-xs text-slate-700 leading-relaxed">
                      {surgery.preOperativeNotes}
                    </div>
                  </div>
                )}

                {/* Doctor Consultation Info Preview */}
                {surgery.appointments && surgery.appointments.length > 0 && (
                  <div className="pt-2 border-t border-slate-100 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                        <Stethoscope className="w-3.5 h-3.5 text-teal-600" /> Latest Doctor Evaluation
                      </span>
                      <button
                        type="button"
                        onClick={() => setActiveTab('consultations')}
                        className="text-xs font-bold text-[#143E78] hover:underline cursor-pointer flex items-center gap-1"
                      >
                        <span>View All ({surgery.appointments.length})</span>
                        <ChevronRight className="w-3 h-3" />
                      </button>
                    </div>

                    <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-100 flex items-center justify-between">
                      <div>
                        <span className="font-bold text-xs text-slate-900 block">
                          {surgery.appointments[0].appointmentDoctorId?.user
                            ? `Dr. ${surgery.appointments[0].appointmentDoctorId.user.firstName} ${surgery.appointments[0].appointmentDoctorId.user.lastName || ''}`
                            : (surgery.appointments[0].doctorName || 'Assigned Consultant')}
                        </span>
                        <span className="text-[11px] text-slate-500">
                          {surgery.appointments[0].appointmentDoctorId?.specialization || 'Consultation'} • {formatDate(surgery.appointments[0].appointmentDate || surgery.appointments[0].date)}
                        </span>
                      </div>
                      <span className="px-2.5 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full text-[10px] font-bold uppercase">
                        {surgery.appointments[0].status || 'Scheduled'}
                      </span>
                    </div>

                    {/* Prescription Medicines & Doctor Advice Preview if present */}
                    {surgery.appointments[0]?.prescriptionId && (
                      <div className="p-3.5 bg-teal-50/50 rounded-xl border border-teal-100 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-bold text-teal-900 flex items-center gap-1.5">
                            <ClipboardList className="w-3.5 h-3.5 text-teal-700" /> Prescribed Medications & Advice
                          </span>
                          {surgery.appointments[0].prescriptionId?._id && (
                            <button
                              type="button"
                              onClick={() => navigate(`/prescriptions/${surgery.appointments[0].prescriptionId._id || surgery.appointments[0].prescriptionId}`)}
                              className="text-[11px] font-bold text-teal-800 hover:underline inline-flex items-center gap-1 cursor-pointer"
                            >
                              <span>Full Prescription</span>
                              <ArrowUpRight className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                        {surgery.appointments[0].prescriptionId?.prescriptionItems?.length > 0 ? (
                          <div className="space-y-1.5 pt-1">
                            {surgery.appointments[0].prescriptionId.prescriptionItems.map((med, mIdx) => (
                              <div key={mIdx} className="text-xs bg-white px-3 py-2 rounded-lg border border-teal-100/80 flex items-center justify-between">
                                <div>
                                  <span className="font-bold text-slate-900">{med.medicineName || med.name}</span>
                                  {(med.dosage || med.frequency) && (
                                    <span className="text-slate-500 ml-2">({med.dosage || ''} {med.frequency || ''})</span>
                                  )}
                                </div>
                                <span className="text-[11px] font-semibold text-slate-600">{med.duration || med.instructions || 'As prescribed'}</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-teal-800">
                            {surgery.appointments[0].prescriptionId?.notes || 'Prescription recorded on file.'}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right Column: Demographics & Registration Profile */}
          <div className="space-y-5">
            {/* 📞 CALL & FOLLOW-UP STATUS CARD */}
            <div className="bg-white rounded-3xl border border-slate-200 p-5 shadow-xs space-y-4">
              <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                <h3 className="text-xs font-black text-slate-900 uppercase tracking-wider flex items-center gap-2">
                  <div className="w-6 h-6 rounded-lg bg-violet-50 text-violet-600 flex items-center justify-center">
                    <PhoneCall className="w-3.5 h-3.5" />
                  </div>
                  <span>Call & Follow-up Status</span>
                </h3>
                {latestCall && (
                  <button
                    type="button"
                    onClick={() => handleOpenQuickDispModal()}
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-violet-700 hover:text-violet-800 bg-violet-50 hover:bg-violet-100 px-2.5 py-1 rounded-xl border border-violet-200 transition-all cursor-pointer shadow-2xs"
                  >
                    <Edit className="w-3 h-3" />
                    <span>Update</span>
                  </button>
                )}
              </div>

              {latestCall ? (
                <div className="space-y-2.5 text-xs">
                  {/* Call Status Row */}
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-slate-500 font-medium">Call Status:</span>
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold border ${getLightStatusBadge(latestCall.status).bg}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${getLightStatusBadge(latestCall.status).dot}`} />
                      {getLightStatusBadge(latestCall.status).label}
                    </span>
                  </div>

                  {/* Follow-up Status Row */}
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-slate-500 font-medium">Follow-up:</span>
                    <button
                      type="button"
                      onClick={() => handleOpenQuickDispModal()}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold border transition-all hover:opacity-90 cursor-pointer ${getLightDispositionBadge(getEffectiveDisposition(latestCall)).bg}`}
                      title="Click to update follow-up status"
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${getLightDispositionBadge(getEffectiveDisposition(latestCall)).dot}`} />
                      {getLightDispositionBadge(getEffectiveDisposition(latestCall)).label}
                      <Edit className="w-3 h-3 ml-0.5 opacity-60" />
                    </button>
                  </div>

                  {/* Time & Direction */}
                  <div className="flex items-center justify-between text-[11px] text-slate-500 pt-2 border-t border-slate-100">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3 text-slate-400" />
                      {new Date(latestCall.createdAt || latestCall.startTime).toLocaleString('en-IN', {
                        day: '2-digit', month: 'short', year: 'numeric',
                        hour: '2-digit', minute: '2-digit', hour12: true
                      })}
                    </span>
                    <span className="capitalize font-bold text-slate-600">
                      {latestCall.direction || 'Inbound'} {latestCall.duration > 0 ? `• ${Math.floor(latestCall.duration / 60)}m ${latestCall.duration % 60}s` : ''}
                    </span>
                  </div>

                  {/* Follow-up Notes if any */}
                  {(latestCall.dispositionNotes || latestCall.notes) && (
                    <div className="bg-slate-50 p-2.5 rounded-xl border border-slate-200/80 text-[11px] text-slate-700 space-y-1">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Follow-up Note:</span>
                      <p className="italic text-slate-600">"{latestCall.dispositionNotes || latestCall.notes}"</p>
                      {latestCall.dispositionUpdatedBy && (
                        <p className="text-[10px] text-slate-400 text-right">
                          — {latestCall.dispositionUpdatedBy.firstName} {latestCall.dispositionUpdatedBy.lastName}
                        </p>
                      )}
                    </div>
                  )}

                  <div className="pt-2 border-t border-slate-100 flex items-center justify-between">
                    <button
                      type="button"
                      onClick={() => setActiveTab('calls')}
                      className="text-xs font-bold text-[#143E78] hover:underline flex items-center gap-1 cursor-pointer"
                    >
                      <span>View All Calls ({callLogs.length})</span>
                      <ChevronRight className="w-3 h-3" />
                    </button>
                    {patientPhone && (
                      <button
                        type="button"
                        onClick={handleExotelCall}
                        disabled={isCallingPatient}
                        className="text-xs font-bold text-emerald-700 hover:text-emerald-800 flex items-center gap-1 cursor-pointer disabled:opacity-50"
                      >
                        <PhoneCall className="w-3 h-3 text-emerald-600" />
                        <span>Dial Again</span>
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="p-3 bg-slate-50 border border-dashed border-slate-200 rounded-xl flex items-center justify-between text-xs text-slate-500">
                  <span className="flex items-center gap-1.5 text-slate-500">
                    <PhoneOff className="w-3.5 h-3.5 text-slate-400" />
                    No call records yet
                  </span>
                  {patientPhone && (
                    <button
                      type="button"
                      onClick={handleExotelCall}
                      disabled={isCallingPatient}
                      className="text-xs font-bold text-[#143E78] hover:underline cursor-pointer"
                    >
                      Call Now
                    </button>
                  )}
                </div>
              )}
            </div>

            <div className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-2xs space-y-4">
              <div className="flex items-center justify-between border-b border-slate-100 pb-2.5">
                <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider">
                  Patient Demographics & Identifiers
                </h3>
                {canEditPatient && (
                  <button
                    type="button"
                    onClick={() => setShowEditPatientModal(true)}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold text-[#143E78] bg-blue-50 hover:bg-blue-100 border border-blue-200/80 transition-all cursor-pointer"
                    title="Edit Patient Demographics"
                  >
                    <Edit className="w-3 h-3 text-[#143E78]" />
                    <span>Edit</span>
                  </button>
                )}
              </div>

              <div className="space-y-3 text-xs">
                <div className="flex justify-between py-1.5 border-b border-slate-50">
                  <span className="text-slate-500">Full Name</span>
                  <span className="font-bold text-slate-800">{patientName}</span>
                </div>
                <div className="flex justify-between py-1.5 border-b border-slate-50">
                  <span className="text-slate-500">Contact Number</span>
                  <span className="font-bold text-slate-800">{patientPhone || '—'}</span>
                </div>
                <div className="flex justify-between py-1.5 border-b border-slate-50">
                  <span className="text-slate-500">Email Address</span>
                  <span className="font-bold text-slate-800">{surgery.lead?.patientEmail || '—'}</span>
                </div>
                <div className="flex justify-between py-1.5 border-b border-slate-50">
                  <span className="text-slate-500">Date of Birth</span>
                  <span className="font-bold text-slate-800">{formatDate(surgery.lead?.dateOfBirth)}</span>
                </div>
                <div className="flex justify-between py-1.5 border-b border-slate-50">
                  <span className="text-slate-500">Emergency Contact</span>
                  <span className="font-bold text-slate-800">{surgery.lead?.emergencyContact || '—'}</span>
                </div>
                <div className="flex justify-between py-1.5 border-b border-slate-50">
                  <span className="text-slate-500">ABHA Health ID</span>
                  <span className="font-mono font-bold text-slate-800">{surgery.lead?.abhaId || '—'}</span>
                </div>
                <div className="flex justify-between py-1.5 border-b border-slate-50">
                  <span className="text-slate-500">Payment / Coverage</span>
                  <span className="font-bold text-slate-800 capitalize">{surgery.lead?.coverageMode?.replace(/_/g, ' ') || 'Self Pay'}</span>
                </div>
                <div className="py-1.5">
                  <span className="text-slate-500 block mb-0.5">Residential Address</span>
                  <span className="font-medium text-slate-800 leading-snug">{fullAddress || '—'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 📞 TAB: CALL HISTORY */}
      {activeTab === 'calls' && (
        <div className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-2xs space-y-4">
          <div className="flex items-center justify-between border-b border-slate-100 pb-3 flex-wrap gap-2">
            <div>
              <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                <PhoneCall className="w-4 h-4 text-violet-600" />
                Exotel Telephony & Follow-up History
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Complete log of inbound and outbound calls, audio recordings, and follow-up resolutions.
              </p>
            </div>
            {patientPhone && (
              <button
                type="button"
                onClick={handleExotelCall}
                disabled={isCallingPatient}
                className="px-3.5 py-1.5 bg-violet-600 hover:bg-violet-700 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all shadow-sm cursor-pointer disabled:opacity-50"
              >
                {isCallingPatient ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PhoneCall className="w-3.5 h-3.5" />}
                <span>Call Patient Now</span>
              </button>
            )}
          </div>

          <CallHistoryPanel
            leadId={targetLeadId}
            refreshTrigger={callHistoryRefresh}
            onLatestCallChange={setLatestCall}
          />
        </div>
      )}

      {/* 📅 TAB: DOCTOR CONSULTATIONS & PRESCRIPTIONS */}
      {activeTab === 'consultations' && (
        <div className="space-y-5">
          {surgery.appointments && surgery.appointments.length > 0 ? (
            surgery.appointments.map((appt, aIdx) => {
              const docUser = appt.appointmentDoctorId?.user;
              const docName = docUser ? `Dr. ${docUser.firstName || ''} ${docUser.lastName || ''}`.trim() : (appt.doctorName || 'Assigned Doctor');
              const rx = appt.prescriptionId;

              return (
                <div key={appt._id || aIdx} className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-2xs space-y-4">
                  <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-teal-50 text-teal-700 flex items-center justify-center font-black">
                        <Stethoscope className="w-5 h-5" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="text-sm font-black text-slate-900">{docName}</h4>
                          <span className="text-xs text-slate-500">• {appt.appointmentDoctorId?.specialization || 'Consultant'}</span>
                        </div>
                        <span className="text-[11px] font-bold text-slate-400">Booking ID: {appt.bookingId || appt._id}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <span className={`px-2.5 py-1 rounded-lg text-xs font-black uppercase tracking-wider ${appt.status === 'completed'
                          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                          : 'bg-blue-50 text-blue-700 border border-blue-200'
                        }`}>
                        {appt.status?.replace(/_/g, ' ') || 'Consultation'}
                      </span>
                      <button
                        type="button"
                        onClick={() => navigate(`/appointments/${appt._id}`)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-black text-[#143E78] bg-slate-100 hover:bg-slate-200 rounded-xl transition-all cursor-pointer"
                      >
                        <span>Open Appointment Hub</span>
                        <ExternalLink className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 text-xs">
                    <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-0.5">Date & Slot</span>
                      <p className="font-bold text-slate-800">{formatDate(appt.appointmentDate)} • {appt.appointmentTimeSlot || '03:00 PM'}</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-0.5">Consultation Type</span>
                      <p className="font-bold text-slate-800 capitalize">{appt.appointmentType?.replace(/_/g, ' ') || 'Consultation'}</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-0.5">Reported Symptoms</span>
                      <p className="font-bold text-slate-800">{appt.symptoms || appt.notes || surgery.lead?.chiefComplaints || 'N/A'}</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-0.5">Consultation Fee</span>
                      <p className="font-bold text-emerald-700">₹{Number(appt.doctorFee || appt.finalPrice || 0).toLocaleString('en-IN')}</p>
                    </div>
                  </div>

                  {/* Prescription Section */}
                  {rx && (
                    <div className="p-4 bg-teal-50/40 rounded-xl border border-teal-100 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-black text-teal-950 flex items-center gap-1.5">
                          <ClipboardList className="w-4 h-4 text-teal-700" /> Prescribed Medications & Investigations
                        </span>
                        {rx._id && (
                          <button
                            type="button"
                            onClick={() => navigate(`/prescriptions/${rx._id}`)}
                            className="text-xs font-black text-teal-800 hover:underline inline-flex items-center gap-1 cursor-pointer"
                          >
                            <span>View Full Prescription</span>
                            <ArrowUpRight className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>

                      {rx.prescriptionItems?.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {rx.prescriptionItems.map((med, mIdx) => (
                            <div key={mIdx} className="bg-white p-3 rounded-lg border border-teal-100 text-xs flex justify-between items-center">
                              <div>
                                <span className="font-black text-slate-900 block">{med.medicineName || med.name}</span>
                                <span className="text-slate-500">{med.dosage || ''} {med.frequency ? `• ${med.frequency}` : ''}</span>
                              </div>
                              <span className="text-[11px] font-bold text-slate-600 bg-slate-100 px-2 py-0.5 rounded">{med.duration || 'As directed'}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-teal-900">{rx.notes || 'Prescription completed on file.'}</p>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center space-y-3">
              <Calendar className="w-10 h-10 text-slate-300 mx-auto" />
              <h4 className="text-sm font-black text-slate-800">No Doctor Consultations Recorded</h4>
              <p className="text-xs text-slate-500">There are no prior consultations or appointments logged for this patient lead.</p>
            </div>
          )}
        </div>
      )}

      {/* 🩺 TAB 2: CARE TEAM & FACILITY */}
      {activeTab === 'team' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {/* Operating Surgeon Card */}
          <div className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-2xs space-y-4">
            <div className="flex items-center gap-3 border-b border-slate-100 pb-3">
              <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center">
                <Stethoscope className="w-5 h-5" />
              </div>
              <div>
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Lead Operating Surgeon</span>
                <h4 className="text-sm font-bold text-slate-900">
                  {surgery.surgeon?.user ? `Dr. ${surgery.surgeon.user.firstName} ${surgery.surgeon.user.lastName || ''}` : 'Pending Assignment'}
                </h4>
              </div>
            </div>
            {surgery.surgeon?.user ? (
              <div className="space-y-2 text-xs">
                <p className="text-slate-600 font-medium">{surgery.surgeon.specialization || 'Surgeon Specialist'}</p>
                {surgery.surgeon.user.phone && <p className="text-slate-600">📞 {surgery.surgeon.user.phone}</p>}
                {surgery.surgeon.user.email && <p className="text-slate-600">📧 {surgery.surgeon.user.email}</p>}
                <Link to={`/doctors/${surgery.surgeon._id}`} className="text-[#143E78] font-bold inline-flex items-center gap-1 hover:underline pt-2">
                  View Doctor Profile <ArrowUpRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            ) : (
              <p className="text-xs text-slate-400 italic">No surgeon assigned to this case yet.</p>
            )}
          </div>

          {/* Anesthesiologist Card */}
          <div className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-2xs space-y-4">
            <div className="flex items-center gap-3 border-b border-slate-100 pb-3">
              <div className="w-10 h-10 rounded-xl bg-purple-50 text-purple-700 flex items-center justify-center">
                <Syringe className="w-5 h-5" />
              </div>
              <div>
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Anesthesiologist</span>
                <h4 className="text-sm font-bold text-slate-900">
                  {surgery.anesthesiologist?.user ? `Dr. ${surgery.anesthesiologist.user.firstName} ${surgery.anesthesiologist.user.lastName || ''}` : 'Pending Assignment'}
                </h4>
              </div>
            </div>
            {surgery.anesthesiologist?.user ? (
              <div className="space-y-2 text-xs">
                <p className="text-slate-600 font-medium">{surgery.anesthesiologist.specialization || 'Anesthesia Specialist'}</p>
                {surgery.anesthesiologist.user.phone && <p className="text-slate-600">📞 {surgery.anesthesiologist.user.phone}</p>}
                {surgery.anesthesiologist.user.email && <p className="text-slate-600">📧 {surgery.anesthesiologist.user.email}</p>}
              </div>
            ) : (
              <p className="text-xs text-slate-400 italic">No anesthesiologist assigned yet.</p>
            )}
          </div>

          {/* Partner Hospital / Facility */}
          <div className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-2xs space-y-4">
            <div className="flex items-center gap-3 border-b border-slate-100 pb-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center">
                <Building2 className="w-5 h-5" />
              </div>
              <div>
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Partner Hospital</span>
                <h4 className="text-sm font-bold text-slate-900">
                  {surgery.hospital?.hospitalName || 'Pending Assignment'}
                </h4>
              </div>
            </div>
            {surgery.hospital?.hospitalName ? (
              <div className="space-y-2 text-xs">
                {surgery.hospital.address?.city && <p className="text-slate-600">📍 {surgery.hospital.address.city}, {surgery.hospital.address.state || ''}</p>}
                {surgery.hospital.contactPerson?.name && <p className="text-slate-600">👤 Contact: {surgery.hospital.contactPerson.name}</p>}
                {surgery.hospital.phone && <p className="text-slate-600">📞 {surgery.hospital.phone}</p>}
              </div>
            ) : (
              <p className="text-xs text-slate-400 italic">No hospital assigned yet.</p>
            )}
          </div>
        </div>
      )}

      {/* 💳 TAB 3: BILLING & PAYMENTS */}
      {activeTab === 'billing' && (
        <div className="space-y-5">
          {/* Billing Overview Strip */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-2xs">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Total Procedure Amount</span>
              <p className="text-lg font-bold text-slate-900">₹{(surgery.surgeryCost || 0).toLocaleString('en-IN')}</p>
            </div>
            <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-2xs">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Payment Status</span>
              <p className="text-lg font-bold text-emerald-700 capitalize">{(surgery.status === 'paid' || surgery.status === 'completed') ? 'Settled' : 'In Progress'}</p>
            </div>
            <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-2xs">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Verified Receipts</span>
              <p className="text-lg font-bold text-[#143E78]">{surgery.paymentHistory?.length || 0} Transactions</p>
            </div>
          </div>

          {/* Transactions Table */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-2xs overflow-hidden">
            <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
              <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider">Payment Transaction History</h4>
            </div>
            {surgery.paymentHistory?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-100 text-xs">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-2.5 text-left font-bold text-slate-500">Date & Time</th>
                      <th className="px-4 py-2.5 text-left font-bold text-slate-500">Amount</th>
                      <th className="px-4 py-2.5 text-left font-bold text-slate-500">Method</th>
                      <th className="px-4 py-2.5 text-left font-bold text-slate-500">Transaction ID</th>
                      <th className="px-4 py-2.5 text-right font-bold text-slate-500">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {surgery.paymentHistory.map((p, idx) => (
                      <tr key={idx} className="hover:bg-slate-50/50">
                        <td className="px-4 py-3 font-medium text-slate-700">{formatDate(p.createdAt)}</td>
                        <td className="px-4 py-3 font-bold text-slate-900">₹{p.amount?.toLocaleString('en-IN')}</td>
                        <td className="px-4 py-3 text-slate-600">{p.paymentMethod || 'Online / Gateway'}</td>
                        <td className="px-4 py-3 font-mono text-slate-500">{p.razorpayPaymentId || p.transactionId || 'DIRECT'}</td>
                        <td className="px-4 py-3 text-right">
                          <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded text-[10px] font-bold uppercase">
                            {p.status || 'Paid'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-8 text-center text-slate-400 text-xs font-medium">
                No payment transactions recorded for this patient case.
              </div>
            )}
          </div>
        </div>
      )}

      {/* 📂 TAB 4: CLINICAL DOCUMENTS */}
      {activeTab === 'documents' && (
        <div className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-2xs space-y-4">
          <div className="flex items-center justify-between border-b border-slate-100 pb-3">
            <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider">
              Attached Medical Reports & Prescriptions
            </h4>
          </div>
          {surgery.lead?.documents?.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {surgery.lead.documents.map((doc, idx) => (
                <a
                  key={idx}
                  href={doc.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl hover:border-blue-400 hover:bg-white transition-all flex items-center justify-between group"
                >
                  <div className="flex items-center gap-3 overflow-hidden">
                    <FileText className="w-5 h-5 text-blue-600 shrink-0" />
                    <div className="truncate">
                      <span className="text-[10px] font-bold text-slate-400 uppercase block truncate">{doc.type || 'Clinical Report'}</span>
                      <p className="text-xs font-bold text-slate-800 truncate">{doc.name || 'View Document'}</p>
                    </div>
                  </div>
                  <ExternalLink className="w-3.5 h-3.5 text-slate-400 group-hover:text-blue-600 shrink-0 ml-2" />
                </a>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center text-slate-400 text-xs font-medium bg-slate-50 rounded-xl">
              No clinical documents or reports uploaded yet.
            </div>
          )}
        </div>
      )}

      {/* 📝 TAB 5: CLINICAL NOTES */}
      {activeTab === 'notes' && (
        <div className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-2xs space-y-4">
          <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider border-b border-slate-100 pb-3">
            Physician & Operative Notes
          </h4>
          <div className="space-y-4 text-xs">
            <div>
              <span className="font-bold text-slate-700 block mb-1">Pre-Operative Instructions & Notes</span>
              <div className="p-3.5 bg-slate-50 border border-slate-100 rounded-xl text-slate-700 leading-relaxed">
                {surgery.preOperativeNotes || 'No pre-operative notes logged.'}
              </div>
            </div>
            <div>
              <span className="font-bold text-slate-700 block mb-1">Post-Operative & Discharge Instructions</span>
              <div className="p-3.5 bg-slate-50 border border-slate-100 rounded-xl text-slate-700 leading-relaxed">
                {surgery.postOperativeNotes || 'No post-operative notes logged.'}
              </div>
            </div>
            {surgery.complications && (
              <div>
                <span className="font-bold text-rose-700 block mb-1">Complications / Critical Remarks</span>
                <div className="p-3.5 bg-rose-50 border border-rose-200 rounded-xl text-rose-900 leading-relaxed">
                  {surgery.complications}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 🔐 MODALS (OTP & Complete Surgery) */}
      {showOTPModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md border border-slate-100">
            <h2 className="text-base font-bold text-slate-900 mb-1">Verify OT Start OTP</h2>
            <p className="text-xs text-slate-500 mb-6">Enter the 6-digit OTP sent to verify doctor presence at the OT.</p>
            <div className="space-y-4">
              <input
                type="text"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value)}
                placeholder="Enter 6-digit OTP"
                maxLength="6"
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-bold tracking-[0.5em] text-center text-lg focus:outline-none focus:ring-2 focus:ring-[#143E78]"
              />
              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => setShowOTPModal(false)}
                  className="flex-1 py-2.5 text-xs font-bold text-slate-600 hover:bg-slate-50 rounded-xl cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  onClick={handleVerifyOTP}
                  disabled={updating}
                  className="flex-1 py-2.5 bg-[#143E78] text-white text-xs font-bold rounded-xl hover:bg-[#0f2e5a] disabled:opacity-50 cursor-pointer"
                >
                  {updating ? 'Verifying...' : 'Verify OTP'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showCompleteModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md border border-slate-100">
            <h2 className="text-base font-bold text-slate-900 mb-1">Complete Surgical Procedure</h2>
            <p className="text-xs text-slate-500 mb-6">Finalize outcomes and post-operative clinical notes.</p>
            <div className="space-y-4">
              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Outcome</label>
                <select
                  value={outcome}
                  onChange={(e) => setOutcome(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold"
                >
                  <option value="successful">Successful</option>
                  <option value="partially_successful">Partially Successful</option>
                  <option value="unsuccessful">Unsuccessful</option>
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Post-operative Notes</label>
                <textarea
                  value={postOperativeNotes}
                  onChange={(e) => setPostOperativeNotes(e.target.value)}
                  rows="3"
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs"
                  placeholder="Enter specific instructions..."
                />
              </div>
              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => setShowCompleteModal(false)}
                  className="flex-1 py-2.5 text-xs font-bold text-slate-600 hover:bg-slate-50 rounded-xl cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCompleteSurgery}
                  disabled={updating}
                  className="flex-1 py-2.5 bg-emerald-700 text-white text-xs font-bold rounded-xl hover:bg-emerald-800 disabled:opacity-50 cursor-pointer"
                >
                  {updating ? 'Saving...' : 'Finalize'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 📞 QUICK CALL FOLLOW-UP STATUS MODAL */}
      {showQuickDispModal && latestCall && createPortal(
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[100000] flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden border border-slate-200 animate-zoomIn">
            <div className="bg-violet-700 p-5 text-white flex justify-between items-center shrink-0">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center">
                  <PhoneCall className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-base font-bold">Update Follow-up Status</h2>
                  <p className="text-[11px] text-violet-200">{patientName} • {patientPhone}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowQuickDispModal(false)}
                className="p-1.5 hover:bg-white/20 rounded-lg text-white/80 hover:text-white transition-all cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-semibold text-slate-500">Exotel Telephony Status:</span>
                  <span className="font-bold text-slate-700">{getLightStatusBadge(latestCall.status).label}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-semibold text-slate-500">Call Time:</span>
                  <span className="text-slate-600">
                    {new Date(latestCall.createdAt || latestCall.startTime).toLocaleString('en-IN', {
                      day: '2-digit', month: 'short', year: 'numeric',
                      hour: '2-digit', minute: '2-digit', hour12: true
                    })}
                  </span>
                </div>
              </div>

              <form onSubmit={handleSaveQuickDisp} className="space-y-4">
                <div>
                  <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">
                    Follow-up Status <span className="text-rose-500">*</span>
                  </label>
                  <select
                    value={quickDispForm.disposition}
                    onChange={e => setQuickDispForm({ ...quickDispForm, disposition: e.target.value })}
                    className="w-full px-3 py-2.5 border border-slate-300 rounded-xl text-xs font-semibold bg-white text-slate-800 outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100"
                    required
                  >
                    <option value="callback_completed">🟢 Callback Completed / Resolved</option>
                    <option value="follow_up_needed">🔵 Follow-up Needed (Call Later)</option>
                    <option value="appointment_booked">🟣 Appointment Booked</option>
                    <option value="not_interested">⚪ Not Interested</option>
                    <option value="invalid_number">🔴 Invalid / Wrong Phone Number</option>
                    <option value="pending">🟡 Pending Callback</option>
                  </select>
                </div>

                <div>
                  <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">
                    Call Reason / Inquiry
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Surgery consultation, Pricing query"
                    value={quickDispForm.callReason}
                    onChange={e => setQuickDispForm({ ...quickDispForm, callReason: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs bg-white text-slate-800 outline-none focus:border-violet-500"
                  />
                </div>

                <div>
                  <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">
                    Follow-up Notes & Remarks
                  </label>
                  <textarea
                    rows={3}
                    placeholder="e.g. Spoke to patient on 9 Sept. Confirmed details and callback scheduled."
                    value={quickDispForm.dispositionNotes}
                    onChange={e => setQuickDispForm({ ...quickDispForm, dispositionNotes: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs bg-white text-slate-800 outline-none focus:border-violet-500 resize-none"
                  />
                </div>

                <div className="pt-3 border-t border-slate-100 flex justify-end gap-2">
                  <button
                    type="button"
                    disabled={isSavingQuickDisp}
                    onClick={() => setShowQuickDispModal(false)}
                    className="px-4 py-2 border border-slate-200 text-slate-600 rounded-xl text-xs font-semibold hover:bg-slate-50 transition-all cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSavingQuickDisp}
                    className="px-5 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-xl text-xs font-bold transition-all shadow-md shadow-violet-200 flex items-center gap-1.5 disabled:opacity-50 cursor-pointer"
                  >
                    {isSavingQuickDisp ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
                    <span>Save Status</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* 🛑 REJECT / DECLINE LEAD MODAL */}
      {showRejectModal && createPortal(
        <div className="fixed inset-0 z-[100000] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-fadeIn">
          <div className="bg-white rounded-3xl border border-slate-200 shadow-2xl max-w-md w-full overflow-hidden p-6 space-y-4">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center border border-rose-100 shrink-0">
                  <XCircle className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-base font-black text-slate-900">Reject / Decline Lead</h3>
                  <p className="text-xs text-slate-500 mt-0.5">{patientName} • {patientPhone || bookingId}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowRejectModal(false)}
                className="p-1 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3.5 pt-1">
              <div>
                <label className="block text-[11px] font-black text-slate-700 uppercase tracking-wider mb-1.5">
                  Rejection Reason <span className="text-rose-500">*</span>
                </label>
                <select
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 bg-white text-xs font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500"
                >
                  <option value="Patient Not Interested / Declined">⚪ Patient Not Interested / Declined Care</option>
                  <option value="Seeking Alternate Hospital / Doctor">🏥 Seeking Alternate Hospital or Doctor</option>
                  <option value="Pricing / Budget Constraint">💰 Pricing / Budget Constraint</option>
                  <option value="Out of Coverage / Service Area">📍 Out of Service Coverage Area</option>
                  <option value="Duplicate / Invalid Inquiry">⚠️ Duplicate or Test Inquiry</option>
                  <option value="No Response / Phone Unreachable">📞 Unreachable after repeated calls</option>
                  <option value="Other Reason">📝 Other Clinical / Operational Reason</option>
                </select>
              </div>

              <div>
                <label className="block text-[11px] font-black text-slate-700 uppercase tracking-wider mb-1.5">
                  Clinical / Operational Note (Optional)
                </label>
                <textarea
                  rows={3}
                  value={rejectNotes}
                  onChange={(e) => setRejectNotes(e.target.value)}
                  placeholder="e.g. Patient decided to undergo treatment at local government hospital."
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 bg-white text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 resize-none"
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setShowRejectModal(false)}
                disabled={isRejecting}
                className="px-4 py-2 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleRejectLead}
                disabled={isRejecting}
                className="px-5 py-2 rounded-xl text-xs font-black bg-rose-600 hover:bg-rose-700 text-white shadow-md shadow-rose-600/20 transition-all flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
              >
                {isRejecting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <XCircle className="w-3.5 h-3.5" />}
                <span>Confirm Rejection</span>
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ✏️ EDIT PATIENT DETAILS MODAL */}
      <EditPatientModal
        isOpen={showEditPatientModal}
        onClose={() => setShowEditPatientModal(false)}
        leadId={targetLeadId}
        initialData={{
          patientName,
          patientPhone,
          patientEmail: surgery?.lead?.patientEmail,
          patientAge,
          patientGender,
          bloodGroup,
          dateOfBirth: surgery?.lead?.dateOfBirth,
          emergencyContact: surgery?.lead?.emergencyContact,
          abhaId: surgery?.lead?.abhaId,
          city: surgery?.lead?.city,
          patientAddress: surgery?.lead?.patientAddress,
          treatmentRequired: surgery?.lead?.treatmentRequired || surgery?.surgeryName,
          disease: surgery?.lead?.disease,
          coverageMode: surgery?.lead?.coverageMode
        }}
        onSuccess={() => {
          fetchSurgery();
        }}
      />
    </div>
  );
};

export default SurgeryDetail;